/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { capitalize } from 'lodash';
import { Check, RotateCcw, X } from 'lucide-react';
import {
  CorrectReleaseRequestEditsInnerOpEnum,
  CorrectRecordingRequestEditsInnerOpEnum,
  CreateReleaseAndLinkRequestSourceEnum,
  CreateArtistAndLinkRequestSourceEnum,
  CreateArtistAndLinkRequestFeaturedStatusEnum,
  ChangeAssociatedArtistsRequestEditsInnerOpEnum,
  ManageContributionsRequestEditsInnerOpEnum,
  RecordingWestburyContributionsInner,
} from '../../../../../shared_components/generated/admin';
import type {
  CorrectRecordingRequestEditsInner,
  ChangeAssociatedArtistsRequestEditsInner,
  ManageContributionsRequestEditsInner,
} from '../../../../../shared_components/generated/admin';
import { LookupLookupReleaseResponseSourceEnum } from '../../../../../shared_components/generated/admin/apis/PortalApi';
import type { Row } from '../MasterDiscographyTypes';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import WModal from 'shared_components/components/WModal';
import { useApi } from 'shared_components/context';

// Artist with featured status and optional id (from WMC artist lookup)
type FeaturedStatus = 'fa' | 'ofa' | 'nf';
type ArtistEntry = {
  name: string;
  isFeatured: boolean;
  featuredStatus: FeaturedStatus;
  id?: string;
};

// Confidence category styling (same as main table / ContributionPill)
const CONFIDENCE_CATEGORIES: {
  id: string;
  label: string;
  color: string;
  textColor: string;
}[] = [
  {
    id: '1',
    label: 'Verified (No Effort)',
    color: '#6EE038',
    textColor: 'black',
  },
  {
    id: '2',
    label: 'Inferred (Low Effort)',
    color: '#F6FF00',
    textColor: 'black',
  },
  {
    id: '3',
    label: 'Credited (Mid Effort)',
    color: '#FF8A00',
    textColor: 'white',
  },
  {
    id: '4',
    label: 'Unverified (High Effort)',
    color: '#FF2600',
    textColor: 'white',
  },
  {
    id: '5',
    label: 'Unknown (Max Effort)',
    color: '#AAB2B9',
    textColor: 'black',
  },
];

function getContributionPillStyle(categoryId: string): {
  backgroundColor: string;
  color: string;
} {
  const cat =
    CONFIDENCE_CATEGORIES.find((c) => c.id === categoryId) ??
    CONFIDENCE_CATEGORIES[4];
  return { backgroundColor: cat.color, color: cat.textColor };
}

function parseContributionEvidenceNotes(confidencePattern: string): {
  evidenceUrl: string;
  notes: string;
} {
  if (!confidencePattern?.trim()) return { evidenceUrl: '', notes: '' };
  try {
    const o = JSON.parse(confidencePattern) as unknown;
    if (typeof o === 'object' && o && !Array.isArray(o)) {
      const obj = o as Record<string, unknown>;
      return {
        evidenceUrl: typeof obj.evidenceUrl === 'string' ? obj.evidenceUrl : '',
        notes: typeof obj.notes === 'string' ? obj.notes : '',
      };
    }
  } catch {
    // ignore
  }
  return { evidenceUrl: '', notes: '' };
}

type ReleaseSourceForm =
  | 'Discogs'
  | 'Deezer'
  | 'Spotify'
  | 'MusicBrainz'
  | 'PPL'
  | 'Other';

function mapReleaseSourceToLookupEnum(
  source: ReleaseSourceForm
):
  | (typeof LookupLookupReleaseResponseSourceEnum)[keyof typeof LookupLookupReleaseResponseSourceEnum]
  | null {
  switch (source) {
    case 'Discogs':
      return LookupLookupReleaseResponseSourceEnum.Discogs;
    case 'Deezer':
      return LookupLookupReleaseResponseSourceEnum.Deezer;
    case 'Spotify':
      return LookupLookupReleaseResponseSourceEnum.Spotify;
    case 'MusicBrainz':
      return LookupLookupReleaseResponseSourceEnum.Mb;
    case 'PPL':
      return LookupLookupReleaseResponseSourceEnum.Ppl;
    default:
      return null;
  }
}

function mapReleaseSourceToCreateEnum(
  source: ReleaseSourceForm
): CreateReleaseAndLinkRequestSourceEnum {
  switch (source) {
    case 'Discogs':
      return CreateReleaseAndLinkRequestSourceEnum.Discogs;
    case 'Deezer':
      return CreateReleaseAndLinkRequestSourceEnum.Deezer;
    case 'Spotify':
      return CreateReleaseAndLinkRequestSourceEnum.Spotify;
    case 'MusicBrainz':
      return CreateReleaseAndLinkRequestSourceEnum.Mb;
    case 'PPL':
      return CreateReleaseAndLinkRequestSourceEnum.Ppl;
    case 'Other':
      return CreateReleaseAndLinkRequestSourceEnum.Other;
    default:
      return CreateReleaseAndLinkRequestSourceEnum.Discogs;
  }
}

const CATALOG_SOURCES: ReleaseSourceForm[] = [
  'Discogs',
  'Deezer',
  'Spotify',
  'MusicBrainz',
  'PPL',
];

export interface RecordingEditFormProps {
  row: Row | null;
  onSave: (row: Row) => void;
  onCancel: () => void;
  /** When true, release and recording sections stack in one column (e.g. for side panel). */
  stacked?: boolean;
  /** When true, do not render the footer (e.g. when used in panel with external fixed footer). */
  hideFooter?: boolean;
  /** When true, show only the Release section (hide Recording & Artist section). */
  releaseOnly?: boolean;
  /** User ID (required for release lookup in Change Release modal). */
  userId?: string;
  availableFormats?: string[];
  availableTypes?: string[];
  availableContributions?: string[];
  availableArtists?: string[];
}

export interface RecordingEditFormHandle {
  save: () => void;
  cancel: () => void;
}

export const RecordingEditForm = forwardRef<
  RecordingEditFormHandle,
  RecordingEditFormProps
>(function RecordingEditForm(
  {
    row,
    onSave,
    onCancel,
    stacked = false,
    hideFooter = false,
    releaseOnly = false,
    userId,
    availableFormats = [],
    availableTypes = [],
    availableContributions = [],
    availableArtists = [],
  },
  ref
) {
  const { adminApi: api } = useApi();
  const [formData, setFormData] = useState<Partial<Row>>({});
  const [labels, setLabels] = useState<string[]>([]);
  const [catNumbers, setCatNumbers] = useState<string[]>([]);
  const [versions, setVersions] = useState<string[]>([]);
  const [isrcs, setIsrcs] = useState<string[]>([]);
  const [artists, setArtists] = useState<ArtistEntry[]>([]);
  const [contributions, setContributions] = useState<
    RecordingWestburyContributionsInner[]
  >([]);
  const [showReleaseCorrectionModal, setShowReleaseCorrectionModal] =
    useState(false);
  const [showChangeReleaseModal, setShowChangeReleaseModal] = useState(false);
  const [releaseId, setReleaseId] = useState('');
  const [releaseSource, setReleaseSource] = useState<
    'Discogs' | 'Deezer' | 'Spotify' | 'MusicBrainz' | 'PPL' | 'Other'
  >('Discogs');
  const [sourceUrl, setSourceUrl] = useState('');
  const [newReleaseTitle, setNewReleaseTitle] = useState('');
  const [newReleaseYear, setNewReleaseYear] = useState('');
  const [newReleaseLabels, setNewReleaseLabels] = useState<string[]>([]);
  const [newReleaseCatNumbers, setNewReleaseCatNumbers] = useState<string[]>(
    []
  );
  const [newReleaseFormat, setNewReleaseFormat] = useState('');
  const [newReleaseType, setNewReleaseType] = useState('');
  const [isReleaseEditable, setIsReleaseEditable] = useState(false);
  const [isRecordingEditable, setIsRecordingEditable] = useState(false);
  const [recordingSaveLoading, setRecordingSaveLoading] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [revertTarget, setRevertTarget] = useState<{
    type: 'section' | 'field';
    field?: string;
  } | null>(null);
  const [releaseLookupLoading, setReleaseLookupLoading] = useState(false);
  const [releaseLookupError, setReleaseLookupError] = useState<string | null>(
    null
  );
  const [releaseLookupSuccess, setReleaseLookupSuccess] = useState(false);
  const [releaseLookupNotFound, setReleaseLookupNotFound] = useState(false);
  const [releaseLookupNotLinked, setReleaseLookupNotLinked] = useState(false);
  const [releaseNotLinkedConfirmed, setReleaseNotLinkedConfirmed] =
    useState(false);
  const [lookupReleaseId, setLookupReleaseId] = useState('');
  const [lookupEdgeId, setLookupEdgeId] = useState<string | null>(null);
  const [lookupDateReleased, setLookupDateReleased] = useState<string | null>(
    null
  );
  const [releaseChangeSubmitLoading, setReleaseChangeSubmitLoading] =
    useState(false);
  const [releaseChangeSubmitError, setReleaseChangeSubmitError] = useState<
    string | null
  >(null);
  const [showLiveToYesModal, setShowLiveToYesModal] = useState(false);
  const [liveToYesNote, setLiveToYesNote] = useState('');
  const [isrcFormatError, setIsrcFormatError] = useState<string | null>(null);
  const [pendingDuplicateIsrc, setPendingDuplicateIsrc] = useState<
    string | null
  >(null);
  const [pendingDuplicateMatches, setPendingDuplicateMatches] = useState<
    { recordingId: string; recordingTitle: string; artistName: string }[]
  >([]);
  const [showCreateArtist, setShowCreateArtist] = useState(false);
  const [newArtistAlias, setNewArtistAlias] = useState('');
  const [newArtistFeatured, setNewArtistFeatured] =
    useState<FeaturedStatus>('fa');
  const [newArtistSource, setNewArtistSource] = useState<
    'discogs' | 'MB' | 'spotify' | 'deezer'
  >('discogs');
  const [newArtistSourceId, setNewArtistSourceId] = useState('');
  const [newArtistIsGroup, setNewArtistIsGroup] = useState(false);
  const [newArtistMembers, setNewArtistMembers] = useState<
    { id: string; stageName: string }[]
  >([]);
  const [newArtistMemberSearch, setNewArtistMemberSearch] = useState('');
  const [newArtistMemberResults, setNewArtistMemberResults] = useState<
    { id: string; stageName: string }[]
  >([]);
  const [newArtistMemberSearchLoading, setNewArtistMemberSearchLoading] =
    useState(false);
  const [newArtistSourceLookupLoading, setNewArtistSourceLookupLoading] =
    useState(false);
  const [newArtistSourceLookupError, setNewArtistSourceLookupError] = useState<
    string | null
  >(null);
  const [pendingArtistName, setPendingArtistName] = useState<string | null>(
    null
  );
  const [pendingArtistId, setPendingArtistId] = useState<string | null>(null);
  const [pendingArtistFeatured, setPendingArtistFeatured] =
    useState<FeaturedStatus>('fa');
  const [editingVersionIndex, setEditingVersionIndex] = useState<number | null>(
    null
  );
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistDropdownOpen, setArtistDropdownOpen] = useState(false);
  const [artistLookupResults, setArtistLookupResults] = useState<
    { id: string; stageName: string }[]
  >([]);
  const [artistLookupLoading, setArtistLookupLoading] = useState(false);
  const [artistsError, setArtistsError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [artistActionModalIndex, setArtistActionModalIndex] = useState<
    number | null
  >(null);
  const [showRemoveContributionModal, setShowRemoveContributionModal] =
    useState<number | null>(null);
  const [addContributionDropdownOpen, setAddContributionDropdownOpen] =
    useState(false);
  const [contributionSearchQuery, setContributionSearchQuery] = useState('');
  const [contributionCategoryOpenIndex, setContributionCategoryOpenIndex] =
    useState<number | null>(null);
  const [categoryDropdownRect, setCategoryDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const [showContributionModal, setShowContributionModal] = useState(false);
  const [contributionModalContext, setContributionModalContext] = useState<
    | { type: 'edit'; index: number }
    | { type: 'add'; contributionValue: string }
    | null
  >(null);
  const [contributionModalForm, setContributionModalForm] = useState<{
    contributionName: string;
    confidenceCategory: string;
    evidenceUrl: string;
    notes: string;
  }>({
    contributionName: '',
    confidenceCategory: '5',
    evidenceUrl: '',
    notes: '',
  });
  const [contributionModalEvidenceError, setContributionModalEvidenceError] =
    useState<string | null>(null);
  const [releaseSaveLoading, setReleaseSaveLoading] = useState(false);
  const [originalValues, setOriginalValues] = useState<{
    formData: Partial<Row>;
    labels: string[];
    catNumbers: string[];
    versions: string[];
    isrcs: string[];
    artists: ArtistEntry[];
    contributions: RecordingWestburyContributionsInner[];
  } | null>(null);

  useEffect(() => {
    const validCount = artists.filter((a) => a.name.trim()).length;
    if (validCount > 0 && artistsError) setArtistsError(null);
  }, [artists, artistsError]);

  // Debounced artist lookup via API (contract 4.4.1)
  useEffect(() => {
    if (!isRecordingEditable || !userId || !row?.id) return;
    const q = artistSearchQuery.trim();
    if (!q && !artistDropdownOpen) return;
    setArtistLookupLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await api.lookupArtistsLookupArtistsResponse({
          userId,
          parentRecordingId: row.id,
          searchTerm: q || '*',
        });
        setArtistLookupResults(
          (result.artists ?? []).map((a) => ({
            id: a.id ?? '',
            stageName: a.stageName ?? '',
          }))
        );
      } catch {
        setArtistLookupResults([]);
      } finally {
        setArtistLookupLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [
    artistSearchQuery,
    artistDropdownOpen,
    isRecordingEditable,
    userId,
    row?.id,
    api,
  ]);

  // Debounced member lookup for group artist creation
  useEffect(() => {
    if (!showCreateArtist || !newArtistIsGroup || !userId || !row?.id) return;
    const q = newArtistMemberSearch.trim();
    if (!q) {
      setNewArtistMemberResults([]);
      return;
    }
    setNewArtistMemberSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await api.lookupArtistsLookupArtistsResponse({
          userId,
          parentRecordingId: row.id,
          searchTerm: q,
        });
        setNewArtistMemberResults(
          (result.artists ?? []).map((a) => ({
            id: a.id ?? '',
            stageName: a.stageName ?? '',
          }))
        );
      } catch {
        setNewArtistMemberResults([]);
      } finally {
        setNewArtistMemberSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [
    newArtistMemberSearch,
    showCreateArtist,
    newArtistIsGroup,
    userId,
    row?.id,
    api,
  ]);

  // Auto-populate stage name from source ID lookup (Discogs API)
  useEffect(() => {
    if (!showCreateArtist || !newArtistSourceId.trim()) {
      setNewArtistSourceLookupError(null);
      return;
    }
    if (newArtistSource !== 'discogs') {
      // Only Discogs lookup is supported currently
      return;
    }
    setNewArtistSourceLookupLoading(true);
    setNewArtistSourceLookupError(null);
    const timer = setTimeout(async () => {
      try {
        const result = await api.searchArtistDiscogsArtist({
          discogsId: newArtistSourceId.trim(),
        });
        if (result.name) {
          setNewArtistAlias(result.name);
          setNewArtistSourceLookupError(null);
        } else {
          setNewArtistSourceLookupError('Artist not found for this ID');
        }
      } catch {
        setNewArtistSourceLookupError('Lookup failed — check the Source ID');
      } finally {
        setNewArtistSourceLookupLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [newArtistSourceId, newArtistSource, showCreateArtist, api]);

  useEffect(() => {
    if (row) {
      setArtistsError(null);
      const initialFormData = {
        release: row.release,
        year: row.year,
        format: row.format,
        mediaType: row.mediaType,
        title: row.title,
        duration: row.duration,
        isLive: row.isLive,
        isFeatured: row.isFeatured,
      };
      setFormData(initialFormData);

      // Parse comma-separated values into arrays
      const initialLabels = row.label
        ? row.label
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v)
        : [];
      const initialCatNumbers = row.catNumbers
        ? row.catNumbers
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v)
        : [];
      const initialVersions = row.version
        ? row.version
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v)
        : [];
      const initialIsrcs = row.isrcs
        ? row.isrcs
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v)
        : [];
      const initialArtists: ArtistEntry[] = row.artist
        ? row.artist
            .split(',')
            .map((name) => ({
              name: name.trim(),
              isFeatured: false,
              featuredStatus: 'nf' as FeaturedStatus,
            }))
            .filter((a) => a.name)
        : [];
      const initialContributions: RecordingWestburyContributionsInner[] =
        row.contributions && row.contributions.length > 0
          ? row.contributions.map((c) => ({
              contribution: c.contribution,
              confidenceCategory: c.confidenceCategory ?? '5',
              confidencePattern: c.confidencePattern ?? '',
            }))
          : [];

      setLabels(initialLabels);
      setCatNumbers(initialCatNumbers);
      setVersions(initialVersions);
      setIsrcs(initialIsrcs);
      setArtists(initialArtists);
      setContributions(initialContributions);
      setShowCreateArtist(false);
      setNewArtistAlias('');
      setNewArtistFeatured('fa');
      setArtistSearchQuery('');
      setArtistDropdownOpen(false);
      setContributionCategoryOpenIndex(null);
      setCategoryDropdownRect(null);
      setShowContributionModal(false);
      setContributionModalContext(null);
      setContributionSearchQuery('');

      // Store original values for revert functionality
      setOriginalValues({
        formData: initialFormData,
        labels: initialLabels,
        catNumbers: initialCatNumbers,
        versions: initialVersions,
        isrcs: initialIsrcs,
        artists: initialArtists,
        contributions: initialContributions,
      });

      // Reset editing state when row changes
      setIsReleaseEditable(false);
      setIsRecordingEditable(false);
    }
  }, [row]);

  const saveHandlerRef = useRef<() => void>(() => {});
  useImperativeHandle(
    ref,
    () => ({
      save: () => saveHandlerRef.current?.(),
      cancel: onCancel,
    }),
    [onCancel]
  );

  if (!row || !originalValues) return null;

  // Helper function to check if a field has been modified
  const isFieldModified = (field: string): boolean => {
    if (!originalValues) return false;

    switch (field) {
      case 'release':
        return formData.release !== originalValues.formData.release;
      case 'year':
        return formData.year !== originalValues.formData.year;
      case 'label':
        return JSON.stringify(labels) !== JSON.stringify(originalValues.labels);
      case 'catNumbers':
        return (
          JSON.stringify(catNumbers) !==
          JSON.stringify(originalValues.catNumbers)
        );
      case 'format':
        return formData.format !== originalValues.formData.format;
      case 'mediaType':
        return formData.mediaType !== originalValues.formData.mediaType;
      case 'artist':
        return (
          JSON.stringify(artists) !== JSON.stringify(originalValues.artists)
        );
      case 'isFeatured':
        return false; // Featured is read-only, never considered modified
      case 'title':
        return formData.title !== originalValues.formData.title;
      case 'version':
        return (
          JSON.stringify(versions) !== JSON.stringify(originalValues.versions)
        );
      case 'isrcs':
        return JSON.stringify(isrcs) !== JSON.stringify(originalValues.isrcs);
      case 'duration':
        return formData.duration !== originalValues.formData.duration;
      case 'isLive':
        return formData.isLive !== originalValues.formData.isLive;
      case 'contributions':
        return (
          JSON.stringify(contributions) !==
          JSON.stringify(originalValues.contributions)
        );
      default:
        return false;
    }
  };

  // Check if any field in release section has been modified
  const isReleaseSectionModified = (): boolean => {
    return (
      isFieldModified('release') ||
      isFieldModified('year') ||
      isFieldModified('label') ||
      isFieldModified('catNumbers') ||
      isFieldModified('format') ||
      isFieldModified('mediaType')
    );
  };

  // Check if any field in recording section has been modified
  const isRecordingSectionModified = (): boolean => {
    return (
      isFieldModified('artist') ||
      isFieldModified('title') ||
      isFieldModified('version') ||
      isFieldModified('isrcs') ||
      isFieldModified('duration') ||
      isFieldModified('isLive') ||
      isFieldModified('isFeatured') ||
      isFieldModified('contributions')
    );
  };

  const handleReleaseLookup = async () => {
    if (!userId || !row?.id || !releaseId.trim()) {
      setReleaseLookupError(
        'User and recording context required. Enter a release ID.'
      );
      return;
    }
    if (releaseSource === 'Other') {
      setReleaseLookupError(
        'Use a catalog source (e.g. Discogs) to look up by ID.'
      );
      return;
    }
    const lookupSource = mapReleaseSourceToLookupEnum(releaseSource);
    if (!lookupSource) {
      setReleaseLookupError('Invalid source for lookup.');
      return;
    }
    setReleaseLookupError(null);
    setReleaseLookupSuccess(false);
    setReleaseLookupNotFound(false);
    setReleaseLookupNotLinked(false);
    setReleaseNotLinkedConfirmed(false);
    setLookupReleaseId('');
    setLookupEdgeId(null);
    setLookupDateReleased(null);
    setReleaseLookupLoading(true);
    try {
      const response = await api.lookupLookupReleaseResponse({
        userId,
        parentRecordingId: row.id,
        sourceId: releaseId.trim(),
        source: lookupSource,
      });
      const p = response.payload;
      setLookupReleaseId(p.releaseId);
      setLookupEdgeId(p.edgeId ?? null);
      setLookupDateReleased(p.dateReleased ?? null);
      if (p.releaseTitle) setNewReleaseTitle(p.releaseTitle);
      if (p.dateReleased) {
        const year =
          p.dateReleased.length >= 4
            ? p.dateReleased.slice(0, 4)
            : p.dateReleased;
        setNewReleaseYear(year);
      }
      if (p.releaseLabels?.length) setNewReleaseLabels([...p.releaseLabels]);
      if (p.catNumbers?.length) setNewReleaseCatNumbers([...p.catNumbers]);
      if (p.format) setNewReleaseFormat(p.format);
      if (p.mediaType) setNewReleaseType(p.mediaType);
      setReleaseLookupSuccess(true);
      if (!response.linked) {
        setReleaseLookupNotLinked(true);
      }
    } catch (err: any) {
      // Detect 404 (release not in system) — the generated client throws a
      // ResponseError whose response.status is 404.
      const status = err?.response?.status ?? err?.status;
      if (status === 404) {
        setReleaseLookupNotFound(true);
      } else {
        const message =
          err instanceof Error ? err.message : 'Release lookup failed';
        setReleaseLookupError(message);
      }
    } finally {
      setReleaseLookupLoading(false);
    }
  };

  const resetReleaseModalState = () => {
    setReleaseId('');
    setSourceUrl('');
    setReleaseSource('Discogs');
    setNewReleaseTitle('');
    setNewReleaseYear('');
    setNewReleaseLabels([]);
    setNewReleaseCatNumbers([]);
    setNewReleaseFormat('');
    setNewReleaseType('');
    setReleaseLookupError(null);
    setReleaseLookupSuccess(false);
    setReleaseLookupNotFound(false);
    setReleaseLookupNotLinked(false);
    setReleaseNotLinkedConfirmed(false);
    setLookupReleaseId('');
    setLookupEdgeId(null);
    setLookupDateReleased(null);
    setReleaseChangeSubmitError(null);
  };

  const mergeReleaseIntoRow = (): Row => {
    if (!row) return {} as Row;
    return {
      ...row,
      ...formData,
      release: newReleaseTitle.trim() || row.release,
      year: newReleaseYear.trim() || row.year,
      label: newReleaseLabels.length ? newReleaseLabels.join(', ') : row.label,
      catNumbers: newReleaseCatNumbers.length
        ? newReleaseCatNumbers.join(', ')
        : row.catNumbers,
      format: newReleaseFormat || row.format,
      mediaType: newReleaseType || row.mediaType,
      isModified: true,
    };
  };

  const handleChangeReleaseSubmit = async () => {
    if (!userId || !row?.id) return;
    const isCreateAndLinkFlow =
      releaseSource === 'Other' || releaseLookupNotFound;
    setReleaseChangeSubmitError(null);
    setReleaseChangeSubmitLoading(true);
    try {
      if (isCreateAndLinkFlow) {
        const createRequest = {
          source: mapReleaseSourceToCreateEnum(releaseSource),
          releaseTitle: newReleaseTitle.trim(),
          dateReleased: newReleaseYear.trim() || undefined,
          releaseLabels:
            newReleaseLabels.length > 0 ? newReleaseLabels : undefined,
          catNumbers:
            newReleaseCatNumbers.length > 0 ? newReleaseCatNumbers : undefined,
          format: newReleaseFormat || undefined,
          mediaType: newReleaseType || undefined,
          ...(releaseSource === 'Other'
            ? { sourceUrl: sourceUrl.trim() }
            : { sourceId: releaseId.trim() }),
        };
        await api.createAndLinkServiceResponse({
          userId,
          parentRecordingId: row.id,
          createReleaseAndLinkRequest: createRequest,
        });
      } else {
        if (!lookupReleaseId) {
          setReleaseChangeSubmitError('Missing release ID from lookup.');
          return;
        }
        await api.linkEarliestServiceResponse({
          userId,
          parentRecordingId: row.id,
          linkReleaseRequest: {
            releaseId: lookupReleaseId,
            edgeId: lookupEdgeId ?? undefined,
            dateReleased: lookupDateReleased ?? undefined,
            releaseTitle: newReleaseTitle.trim(),
            releaseLabels:
              newReleaseLabels.length > 0 ? newReleaseLabels : undefined,
            catNumbers:
              newReleaseCatNumbers.length > 0
                ? newReleaseCatNumbers
                : undefined,
            mediaType: newReleaseType || undefined,
            format: newReleaseFormat || undefined,
          },
        });
      }
      const updatedRow = mergeReleaseIntoRow();
      setFormData((prev) => ({
        ...prev,
        release: newReleaseTitle.trim() || prev.release,
        year: newReleaseYear.trim() || prev.year,
        format: newReleaseFormat || prev.format,
        mediaType: newReleaseType || prev.mediaType,
      }));
      setLabels(newReleaseLabels.length ? newReleaseLabels : labels);
      setCatNumbers(
        newReleaseCatNumbers.length ? newReleaseCatNumbers : catNumbers
      );
      onSave(updatedRow);
      resetReleaseModalState();
      setShowChangeReleaseModal(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Request failed';
      setReleaseChangeSubmitError(message);
    } finally {
      setReleaseChangeSubmitLoading(false);
    }
  };

  const handleRevert = (type: 'section' | 'field', field?: string) => {
    if (type === 'section') {
      // Revert entire section
      if (field === 'release') {
        setFormData((prev) => ({
          ...prev,
          release: originalValues.formData.release,
          year: originalValues.formData.year,
          format: originalValues.formData.format,
          mediaType: originalValues.formData.mediaType,
        }));
        setLabels(originalValues.labels);
        setCatNumbers(originalValues.catNumbers);
      } else if (field === 'recording') {
        setFormData((prev) => ({
          ...prev,
          title: originalValues.formData.title,
          duration: originalValues.formData.duration,
          isLive: originalValues.formData.isLive,
          isFeatured: originalValues.formData.isFeatured,
        }));
        setVersions(originalValues.versions);
        setIsrcs(originalValues.isrcs);
        setArtists(originalValues.artists);
        setContributions(originalValues.contributions);
        setContributionCategoryOpenIndex(null);
        setCategoryDropdownRect(null);
      }
    } else if (type === 'field' && field) {
      // Revert specific field
      switch (field) {
        case 'release':
          handleFieldChange('release', originalValues.formData.release || '');
          break;
        case 'year':
          handleFieldChange('year', originalValues.formData.year || '');
          break;
        case 'label':
          setLabels(originalValues.labels);
          break;
        case 'catNumbers':
          setCatNumbers(originalValues.catNumbers);
          break;
        case 'format':
          handleFieldChange('format', originalValues.formData.format || '');
          break;
        case 'mediaType':
          handleFieldChange(
            'mediaType',
            originalValues.formData.mediaType || ''
          );
          break;
        case 'artist':
          setArtists(originalValues.artists);
          break;
        case 'title':
          handleFieldChange('title', originalValues.formData.title || '');
          break;
        case 'version':
          setVersions(originalValues.versions);
          break;
        case 'isrcs':
          setIsrcs(originalValues.isrcs);
          break;
        case 'duration':
          handleFieldChange('duration', originalValues.formData.duration || '');
          break;
        case 'isLive':
          handleFieldChange('isLive', originalValues.formData.isLive || false);
          break;
        case 'isFeatured':
          handleFieldChange(
            'isFeatured',
            originalValues.formData.isFeatured || false
          );
          break;
        case 'contributions':
          setContributions(originalValues.contributions);
          setContributionCategoryOpenIndex(null);
          setCategoryDropdownRect(null);
          break;
      }
    }
    setShowRevertModal(false);
    setRevertTarget(null);
  };

  const handleReleaseCancel = () => {
    handleRevert('section', 'release');
    onCancel();
  };

  const handleSaveReleaseDetails = async () => {
    if (!userId || !row?.id || !row.earliestParentReleaseId) return;
    setReleaseSaveLoading(true);
    try {
      const edits: Array<{
        op: CorrectReleaseRequestEditsInnerOpEnum;
        field: string;
        value: string;
      }> = [];
      if (isFieldModified('release')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'release_title',
          value: formData.release ?? '',
        });
      }
      if (isFieldModified('year')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'date_released',
          value: formData.year ?? '',
        });
      }
      if (isFieldModified('label')) {
        const origLabels = originalValues?.labels ?? [];
        const addedLabels = labels.filter((v) => !origLabels.includes(v));
        const removedLabels = origLabels.filter((v) => !labels.includes(v));
        addedLabels.forEach((v) => edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Add,
          field: 'release_labels',
          value: v,
        }));
        removedLabels.forEach((v) => edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Delete,
          field: 'release_labels',
          value: v,
        }));
      }
      if (isFieldModified('catNumbers')) {
        const origCatNumbers = originalValues?.catNumbers ?? [];
        const addedCatNumbers = catNumbers.filter((v) => !origCatNumbers.includes(v));
        const removedCatNumbers = origCatNumbers.filter((v) => !catNumbers.includes(v));
        addedCatNumbers.forEach((v) => edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Add,
          field: 'cat_numbers',
          value: v,
        }));
        removedCatNumbers.forEach((v) => edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Delete,
          field: 'cat_numbers',
          value: v,
        }));
      }
      if (isFieldModified('format')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'format',
          value: formData.format ?? '',
        });
      }
      if (isFieldModified('mediaType')) {
        edits.push({
          op: CorrectReleaseRequestEditsInnerOpEnum.Edit,
          field: 'media_type',
          value: formData.mediaType ?? '',
        });
      }
      if (edits.length > 0) {
        await api.correctServiceResponse({
          userId,
          parentRecordingId: row.id,
          correctReleaseRequest: {
            releaseId: row.earliestParentReleaseId,
            edits,
          },
        });
      }
      // Propagate updated values back to the parent/grid
      const updatedRow: Row = {
        ...row,
        ...formData,
        label: labels.join(', '),
        catNumbers: catNumbers.join(', '),
        isModified: true,
      };
      onSave(updatedRow);
    } finally {
      setIsReleaseEditable(false);
      setReleaseSaveLoading(false);
    }
  };

  // ── Recording section handlers ──

  const handleRecordingCancel = () => {
    handleRevert('section', 'recording');
    setIsRecordingEditable(false);
    setShowContributionModal(false);
    setContributionModalContext(null);
    setShowRemoveContributionModal(null);
    setArtistActionModalIndex(null);
    setShowCreateArtist(false);
    setNewArtistAlias('');
    setNewArtistFeatured('fa');
    setNewArtistSource('discogs');
    setNewArtistSourceId('');
    setNewArtistIsGroup(false);
    setNewArtistMembers([]);
    setNewArtistMemberSearch('');
    setNewArtistSourceLookupError(null);
    setPendingArtistName(null);
    setPendingArtistId(null);
    setPendingArtistFeatured('fa');
    setEditingVersionIndex(null);
    setArtistDropdownOpen(false);
    setAddContributionDropdownOpen(false);
    setContributionSearchQuery('');
    setArtistSearchQuery('');
    setIsrcFormatError(null);
    setPendingDuplicateIsrc(null);
  };

  const handleSaveRecordingDetails = async () => {
    if (!formData.title?.trim()) {
      setTitleError('Recording title is required');
      return;
    }
    setTitleError(null);

    const validArtists = artists.filter((a) => a.name.trim());
    if (validArtists.length === 0) {
      setArtistsError('At least one artist is required');
      return;
    }
    setArtistsError(null);

    const durationWasAlreadySet = !!originalValues?.formData?.duration
      ?.toString()
      .trim();
    if (
      !durationWasAlreadySet &&
      formData.duration &&
      !isValidDuration(formData.duration)
    ) {
      alert('Duration must be in HH:MM:SS format (e.g. 00:03:45)');
      return;
    }

    setRecordingSaveLoading(true);
    try {
      // ── 1. Correct recording data (contract 4.1.1) ──
      if (userId && row?.id) {
        const recordingEdits: CorrectRecordingRequestEditsInner[] = [];
        if (isFieldModified('title')) {
          recordingEdits.push({
            op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
            field: 'recordingTitle',
            value: (formData.title ?? '') as unknown as object,
          });
        }
        if (isFieldModified('duration')) {
          recordingEdits.push({
            op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
            field: 'duration',
            value: (formData.duration ?? '') as unknown as object,
          });
        }
        if (isFieldModified('isLive')) {
          // is_live: boolean flag (OS schema: ParentRecording.isLive)
          recordingEdits.push({
            op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
            field: 'is_live',
            value: !!formData.isLive as unknown as object,
          });
          // live: str — the user-provided live info from the modal prompt
          // (OS schema: ParentRecording.live, e.g. "Live in London, 1989")
          if (formData.isLive) {
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
              field: 'live',
              value: liveToYesNote.trim() as unknown as object,
            });
          } else {
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Edit,
              field: 'live',
              value: '' as unknown as object,
            });
          }
        }
        if (isFieldModified('version')) {
          // recordingVersion is list[str] in OS schema.
          // Send delete for removed versions and add for new ones.
          const origVersions = originalValues?.versions ?? [];
          const addedVersions = versions.filter(
            (v) => !origVersions.includes(v)
          );
          const removedVersions = origVersions.filter(
            (v) => !versions.includes(v)
          );
          removedVersions.forEach((v) =>
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Delete,
              field: 'recordingVersion',
              value: v as unknown as object,
            })
          );
          addedVersions.forEach((v) =>
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Add,
              field: 'recordingVersion',
              value: v as unknown as object,
            })
          );
        }
        if (isFieldModified('isrcs')) {
          const origIsrcs = originalValues?.isrcs ?? [];
          const added = isrcs.filter((v) => !origIsrcs.includes(v));
          const removed = origIsrcs.filter((v) => !isrcs.includes(v));
          added.forEach((v) =>
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Add,
              field: 'isrcs',
              value: v as unknown as object,
            })
          );
          removed.forEach((v) =>
            recordingEdits.push({
              op: CorrectRecordingRequestEditsInnerOpEnum.Delete,
              field: 'isrcs',
              value: v as unknown as object,
            })
          );
        }

        if (recordingEdits.length > 0) {
          await api.correctRecordingServiceResponse({
            userId,
            parentRecordingId: row.id,
            correctRecordingRequest: { edits: recordingEdits },
          });
        }

        // ── 2. Change associated artists (contract 4.1.3) ──
        if (isFieldModified('artist')) {
          const origNames = (originalValues?.artists ?? []).map((a) => a.name);
          const currNames = validArtists.map((a) => a.name);
          const artistEdits: ChangeAssociatedArtistsRequestEditsInner[] = [];

          // Added artists — use artist.id from WMC lookup when available
          validArtists
            .filter((a) => !origNames.includes(a.name))
            .forEach((a) => {
              artistEdits.push({
                artistId: a.id || a.name,
                op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Add,
                featuredStatus: (a.featuredStatus ??
                  (a.isFeatured ? 'fa' : 'ofa')) as 'fa' | 'ofa' | 'nfa',
              });
            });

          // Removed artists — null = remove entirely (vs 'nfa' = demote to non-featured)
          // TODO: Add modal to let user choose between remove and demote
          (originalValues?.artists ?? [])
            .filter((a) => !currNames.includes(a.name))
            .forEach((a) => {
              artistEdits.push({
                artistId: a.id || a.name,
                op: ChangeAssociatedArtistsRequestEditsInnerOpEnum.Delete,
                featuredStatus: null,
              });
            });

          if (artistEdits.length > 0) {
            await api.changeArtistsServiceResponse({
              userId,
              parentRecordingId: row.id,
              changeAssociatedArtistsRequest: { edits: artistEdits },
            });
          }
        }

        // ── 3. Manage contributions (contract 4.2.1) ──
        if (isFieldModified('contributions')) {
          const origContribs = originalValues?.contributions ?? [];
          const contribEdits: ManageContributionsRequestEditsInner[] = [];

          // Added contributions
          contributions
            .filter(
              (c) =>
                !origContribs.some((oc) => oc.contribution === c.contribution)
            )
            .forEach((c) => {
              const { evidenceUrl, notes } = parseContributionEvidenceNotes(
                c.confidencePattern ?? ''
              );
              contribEdits.push({
                op: ManageContributionsRequestEditsInnerOpEnum.Add,
                role: c.contribution ?? '',
                dataCategory: c.confidenceCategory ?? '5',
                sourceUrl: evidenceUrl || undefined,
                notes: notes || undefined,
              });
            });

          // Removed contributions
          origContribs
            .filter(
              (oc) =>
                !contributions.some((c) => c.contribution === oc.contribution)
            )
            .forEach((oc) => {
              contribEdits.push({
                op: ManageContributionsRequestEditsInnerOpEnum.Delete,
                role: oc.contribution ?? '',
              });
            });

          // Edited contributions (same role, changed category/pattern)
          contributions
            .filter((c) => {
              const orig = origContribs.find(
                (oc) => oc.contribution === c.contribution
              );
              return (
                orig &&
                (orig.confidenceCategory !== c.confidenceCategory ||
                  orig.confidencePattern !== c.confidencePattern)
              );
            })
            .forEach((c) => {
              const { evidenceUrl, notes } = parseContributionEvidenceNotes(
                c.confidencePattern ?? ''
              );
              contribEdits.push({
                op: ManageContributionsRequestEditsInnerOpEnum.Edit,
                role: c.contribution ?? '',
                dataCategory: c.confidenceCategory ?? '5',
                sourceUrl: evidenceUrl || undefined,
                notes: notes || undefined,
              });
            });

          if (contribEdits.length > 0) {
            await api.manageContributionsServiceResponse({
              userId,
              parentRecordingId: row.id,
              manageContributionsRequest: { edits: contribEdits },
            });
          }
        }
      }

      const updatedRow: Row = {
        ...row,
        ...formData,
        artist: validArtists.map((a) => a.name).join(', '),
        contributions: contributions,
        label: labels.join(', '),
        catNumbers: catNumbers.join(', '),
        version: versions.join(', '),
        isrcs: isrcs.join(', '),
        isModified: true,
      };
      onSave(updatedRow);
    } finally {
      setIsRecordingEditable(false);
      setRecordingSaveLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper functions for managing multiple values
  const addMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    value: string
  ) => {
    const trimmed = value.trim();
    if (trimmed) {
      setter((prev) =>
        prev.some((v) => v.toLowerCase() === trimmed.toLowerCase())
          ? prev
          : [...prev, trimmed]
      );
    }
  };

  const removeMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number
  ) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMultipleValue = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    index: number,
    value: string
  ) => {
    setter((prev) => prev.map((v, i) => (i === index ? value.trim() : v)));
  };

  /** Validate ISRC uniqueness via the backend OpenSearch endpoint. */
  const validateIsrcDuplication = async (
    isrc: string
  ): Promise<{
    isDuplicate: boolean;
    matches: {
      recordingId: string;
      recordingTitle: string;
      artistName: string;
    }[];
  }> => {
    if (!userId || !row?.id) {
      return { isDuplicate: false, matches: [] };
    }
    try {
      const resp = await api.validateIsrcValidateIsrcResponse({
        userId,
        parentRecordingId: row.id,
        validateIsrcRequest: { isrc },
      });
      return {
        isDuplicate: resp.isDuplicate ?? false,
        matches: (resp.matches ?? []).map((m) => ({
          recordingId: m.recordingId ?? '',
          recordingTitle: m.recordingTitle ?? '',
          artistName: m.artistName ?? '',
        })),
      };
    } catch {
      // If the API call fails, allow the ISRC to be added (don't block on network errors)
      return { isDuplicate: false, matches: [] };
    }
  };

  /** Validate ISRC format: 2 letter country code, 3 alphanumeric registrant code,
   *  2 digit year, 5 digit designation code (12 chars total, e.g. GBAAA0912345). */
  const ISRC_REGEX = /^[A-Z]{2}[A-Z0-9]{3}\d{2}\d{5}$/;
  const validateIsrcFormat = (value: string): boolean => {
    return ISRC_REGEX.test(value.toUpperCase().replace(/[-\s]/g, ''));
  };

  const addIsrc = async (raw: string) => {
    if (!validateIsrcFormat(raw)) {
      setIsrcFormatError(
        'Invalid ISRC format. Expected: 2 letter country code, 3 char registrant code, 2 digit year, 5 digit designation (e.g. GBAAA0912345)'
      );
      return;
    }
    setIsrcFormatError(null);
    const { isDuplicate, matches } = await validateIsrcDuplication(raw);
    if (isDuplicate) {
      setPendingDuplicateIsrc(raw);
      setPendingDuplicateMatches(matches);
      return;
    }
    addMultipleValue(setIsrcs, raw);
    const input = document.getElementById(
      'modal-isrcs-input'
    ) as HTMLInputElement;
    if (input) input.value = '';
  };

  /** Validate duration is in HH:MM:SS format. */
  const isValidDuration = (value: string): boolean => {
    if (!value) return true; // empty is allowed (field is optional until filled)
    return /^\d{2}:\d{2}:\d{2}$/.test(value);
  };

  saveHandlerRef.current = handleSaveRecordingDetails;

  return (
    <>
      <div
        className={
          stacked
            ? 'tw-flex tw-flex-col tw-gap-0 tw-mt-2 tw-pb-2'
            : 'tw-grid tw-grid-cols-[1fr_auto_1fr] tw-gap-0 tw-mt-2 tw-pb-2 tw-overflow-y-auto tw-max-h-[calc(90vh-100px)]'
        }
      >
        {!releaseOnly && (
          <>
            {/* Recording section (includes Contributions, Artists, and Recording fields) */}
            <div
              className={
                stacked
                  ? 'tw-flex tw-flex-col tw-shrink-0'
                  : 'tw-flex tw-flex-col tw-overflow-y-auto'
              }
            >
              <div className="tw-px-3 tw-pt-2 tw-pb-2 tw-flex tw-flex-col tw-shrink-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-pb-1.5 tw-border-b tw-border-gray-600 tw-flex-shrink-0">
                  <div className="tw-text-white tw-font-semibold">
                    Recording
                  </div>
                </div>
                <div className="tw-space-y-4">
                  {/* ── Artists subsection ── */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                      <label
                        htmlFor="modal-artist-select"
                        className={`tw-text-sm tw-font-medium ${
                          isFieldModified('artist')
                            ? 'tw-text-orange-400'
                            : 'tw-text-white'
                        }`}
                      >
                        Artists
                      </label>
                      <div className="tw-flex tw-items-center tw-gap-3">
                        {isRecordingEditable && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowCreateArtist(true);
                            }}
                            className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                          >
                            <i className="wi wi-20px wi-plus tw-mr-1" />
                            <span className="tw-underline">Create artist</span>
                          </button>
                        )}
                      </div>
                    </div>
                    {artistsError && (
                      <p className="tw-text-westburyRed tw-text-xs tw-mb-1">
                        {artistsError}
                      </p>
                    )}
                    {artists.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-1.5">
                        {artists.map((artist, index) => {
                          const origArtist = originalValues?.artists?.find(
                            (oa) => oa.name === artist.name
                          );
                          const isArtistAdded = !origArtist;
                          const isArtistEdited =
                            !!origArtist &&
                            origArtist.featuredStatus !== artist.featuredStatus;
                          return (
                            <button
                              key={index}
                              type="button"
                              onClick={() => {
                                if (isRecordingEditable)
                                  setArtistActionModalIndex(index);
                              }}
                              disabled={!isRecordingEditable}
                              className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white tw-transition-colors ${
                                isArtistAdded || isArtistEdited
                                  ? 'tw-border-l-[3px] tw-border-l-surfacePurple'
                                  : ''
                              } ${
                                isRecordingEditable
                                  ? 'tw-cursor-pointer hover:tw-bg-gray-600'
                                  : 'tw-cursor-default'
                              }`}
                              title={
                                isRecordingEditable
                                  ? 'Click to manage artist'
                                  : isArtistAdded
                                  ? 'Newly added artist'
                                  : isArtistEdited
                                  ? 'Edited artist'
                                  : undefined
                              }
                            >
                              <span>{artist.name}</span>
                              <span
                                className={`tw-text-[10px] tw-px-1 tw-rounded ${
                                  artist.featuredStatus === 'fa'
                                    ? 'tw-bg-green-600/20 tw-text-green-400'
                                    : artist.featuredStatus === 'ofa'
                                    ? 'tw-bg-blue-600/20 tw-text-blue-400'
                                    : 'tw-bg-gray-600 tw-text-gray-400'
                                }`}
                              >
                                {artist.featuredStatus === 'fa'
                                  ? 'FA'
                                  : artist.featuredStatus === 'ofa'
                                  ? 'OFA'
                                  : 'NF'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="tw-relative">
                      <input
                        id="modal-artist-select"
                        type="text"
                        value={artistSearchQuery}
                        onChange={(e) => {
                          setArtistSearchQuery(e.target.value);
                          setArtistDropdownOpen(true);
                        }}
                        onFocus={() => {
                          if (isRecordingEditable) setArtistDropdownOpen(true);
                        }}
                        onBlur={() => {
                          setTimeout(() => setArtistDropdownOpen(false), 200);
                        }}
                        placeholder="Search or select artist…"
                        readOnly={!isRecordingEditable}
                        disabled={!isRecordingEditable}
                        className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                          !isRecordingEditable
                            ? 'tw-cursor-not-allowed tw-opacity-60'
                            : ''
                        }`}
                      />
                      {artistDropdownOpen && (
                        <div className="tw-absolute tw-z-50 tw-left-0 tw-right-0 tw-top-full tw-mt-0.5 tw-max-h-48 tw-overflow-y-auto tw-rounded tw-border tw-border-gray-600 tw-bg-gray-800 tw-py-1 tw-shadow-lg">
                          {artistLookupLoading ? (
                            <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                              Searching...
                            </div>
                          ) : (
                            (() => {
                              const notAdded = artistLookupResults.filter(
                                (a) =>
                                  !artists.some(
                                    (e) =>
                                      e.name === a.stageName || e.id === a.id
                                  )
                              );
                              if (notAdded.length === 0) {
                                return (
                                  <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                                    {artistLookupResults.length === 0
                                      ? 'No matching artists'
                                      : 'All matching artists already added'}
                                  </div>
                                );
                              }
                              return notAdded.map((artist) => (
                                <button
                                  key={artist.id}
                                  type="button"
                                  className="tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-gray-700 tw-transition-colors"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setPendingArtistName(artist.stageName);
                                    setPendingArtistId(artist.id);
                                    setPendingArtistFeatured('fa');
                                    setArtistSearchQuery('');
                                    setArtistDropdownOpen(false);
                                  }}
                                >
                                  <span>{artist.stageName}</span>
                                  <span className="tw-text-gray-500 tw-text-xs tw-ml-2">
                                    {artist.id}
                                  </span>
                                </button>
                              ));
                            })()
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Recording Title */}
                  <div>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                      <label
                        htmlFor="modal-title"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('title')
                            ? 'tw-text-orange-400'
                            : 'tw-text-white'
                        }`}
                      >
                        Recording Title
                      </label>
                    </div>
                    <input
                      id="modal-title"
                      type="text"
                      value={formData.title || ''}
                      onChange={(e) => {
                        handleFieldChange('title', e.target.value);
                        if (titleError && e.target.value.trim())
                          setTitleError(null);
                      }}
                      readOnly={!isRecordingEditable}
                      disabled={!isRecordingEditable}
                      className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                        titleError ? 'tw-border-red-500' : 'tw-border-gray-600'
                      } ${
                        !isRecordingEditable
                          ? 'tw-cursor-not-allowed tw-opacity-60'
                          : ''
                      }`}
                    />
                    {titleError && (
                      <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                        {titleError}
                      </p>
                    )}
                  </div>

                  {/* Version (Multiple) */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="modal-version-input"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('version')
                            ? 'tw-text-orange-400'
                            : 'tw-text-white'
                        }`}
                      >
                        Version
                      </label>
                    </div>
                    {versions.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                        {versions.map((version, index) => {
                          const isVersionEdited =
                            originalValues?.versions?.[index] !== undefined &&
                            originalValues.versions[index] !== version;
                          return editingVersionIndex === index ? (
                            <input
                              key={index}
                              type="text"
                              ref={(el) => el?.focus()}
                              value={version}
                              onChange={(e) =>
                                updateMultipleValue(
                                  setVersions,
                                  index,
                                  e.target.value
                                )
                              }
                              onBlur={() => setEditingVersionIndex(null)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === 'Escape') {
                                  setEditingVersionIndex(null);
                                }
                              }}
                              className="tw-bg-gray-700 tw-border tw-border-westburyBlue tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-white tw-outline-none tw-min-w-[60px] tw-max-w-[160px]"
                            />
                          ) : (
                            <div
                              key={index}
                              role={isRecordingEditable ? 'button' : undefined}
                              tabIndex={isRecordingEditable ? 0 : undefined}
                              className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white tw-border-2 tw-border-transparent ${
                                isRecordingEditable
                                  ? 'tw-cursor-pointer hover:tw-border-white/50'
                                  : 'tw-opacity-60'
                              }`}
                              title={
                                isRecordingEditable
                                  ? 'Click to edit'
                                  : undefined
                              }
                              onClick={() => {
                                if (isRecordingEditable)
                                  setEditingVersionIndex(index);
                              }}
                              onKeyDown={(e) => {
                                if (
                                  isRecordingEditable &&
                                  (e.key === 'Enter' || e.key === ' ')
                                ) {
                                  e.preventDefault();
                                  setEditingVersionIndex(index);
                                }
                              }}
                            >
                              {isVersionEdited && (
                                <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-orange-400 tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                              )}
                              <span>{version}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {versions.length === 0 && (
                      <p className="tw-text-gray-400 tw-text-xs">None</p>
                    )}
                  </div>

                  {/* ── Contributions subsection ── */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                      <label
                        htmlFor="modal-contribution-search"
                        className={`tw-text-sm tw-font-medium ${
                          isFieldModified('contributions')
                            ? 'tw-text-orange-400'
                            : 'tw-text-white'
                        }`}
                      >
                        Contributions
                      </label>
                    </div>
                    {contributions.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-1.5">
                        {[...contributions]
                          .map((cont, originalIndex) => ({
                            cont,
                            originalIndex,
                          }))
                          .sort(
                            (a, b) =>
                              parseInt(a.cont.confidenceCategory ?? '5', 10) -
                              parseInt(b.cont.confidenceCategory ?? '5', 10)
                          )
                          .map(({ cont, originalIndex }) => {
                            const style = getContributionPillStyle(
                              cont.confidenceCategory ?? '5'
                            );
                            const origContrib =
                              originalValues?.contributions?.find(
                                (oc) => oc.contribution === cont.contribution
                              );
                            const isAdded = !origContrib;
                            const isEdited =
                              !!origContrib &&
                              (origContrib.confidenceCategory !==
                                cont.confidenceCategory ||
                                origContrib.confidencePattern !==
                                  cont.confidencePattern);
                            return (
                              <div
                                key={originalIndex}
                                className="tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-uppercase tw-border-2 tw-border-transparent tw-transition-colors tw-cursor-pointer hover:tw-border-white/50"
                                style={style}
                                title={
                                  isAdded
                                    ? 'Newly added contribution'
                                    : isEdited
                                    ? 'Edited contribution'
                                    : 'Click to edit confidence category'
                                }
                              >
                                {(isAdded || isEdited) && (
                                  <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-orange-400 tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                                )}
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isRecordingEditable) return;
                                    const c = contributions[originalIndex];
                                    const { evidenceUrl, notes } =
                                      parseContributionEvidenceNotes(
                                        c.confidencePattern ?? ''
                                      );
                                    setContributionModalForm({
                                      contributionName: c.contribution ?? '',
                                      confidenceCategory:
                                        c.confidenceCategory ?? '5',
                                      evidenceUrl,
                                      notes,
                                    });
                                    setContributionModalEvidenceError(null);
                                    setContributionModalContext({
                                      type: 'edit',
                                      index: originalIndex,
                                    });
                                    setShowContributionModal(true);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (!isRecordingEditable) return;
                                      const c = contributions[originalIndex];
                                      const { evidenceUrl, notes } =
                                        parseContributionEvidenceNotes(
                                          c.confidencePattern ?? ''
                                        );
                                      setContributionModalForm({
                                        contributionName: c.contribution ?? '',
                                        confidenceCategory:
                                          c.confidenceCategory ?? '5',
                                        evidenceUrl,
                                        notes,
                                      });
                                      setContributionModalEvidenceError(null);
                                      setContributionModalContext({
                                        type: 'edit',
                                        index: originalIndex,
                                      });
                                      setShowContributionModal(true);
                                    }
                                  }}
                                >
                                  {capitalize(cont.contribution)}
                                </span>
                                {isRecordingEditable &&
                                  contributions.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowRemoveContributionModal(
                                          originalIndex
                                        );
                                      }}
                                      className="tw-ml-0.5 tw-opacity-60 hover:tw-opacity-100 tw-flex tw-items-center"
                                      title="Remove contribution"
                                    >
                                      <X size={12} />
                                    </button>
                                  )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                    <div className="tw-relative">
                      <input
                        id="modal-contribution-search"
                        type="text"
                        value={contributionSearchQuery}
                        onChange={(e) => {
                          setContributionSearchQuery(e.target.value);
                          setAddContributionDropdownOpen(true);
                        }}
                        onFocus={() => {
                          if (isRecordingEditable)
                            setAddContributionDropdownOpen(true);
                        }}
                        onBlur={() => {
                          setTimeout(
                            () => setAddContributionDropdownOpen(false),
                            200
                          );
                        }}
                        placeholder="Search or select contribution…"
                        readOnly={!isRecordingEditable}
                        disabled={!isRecordingEditable}
                        className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                          !isRecordingEditable
                            ? 'tw-cursor-not-allowed tw-opacity-60'
                            : ''
                        }`}
                      />
                      {addContributionDropdownOpen && (
                        <div className="tw-absolute tw-z-50 tw-left-0 tw-right-0 tw-top-full tw-mt-0.5 tw-max-h-48 tw-overflow-y-auto tw-rounded tw-border tw-border-gray-600 tw-bg-gray-800 tw-py-1 tw-shadow-lg">
                          {(() => {
                            const q = contributionSearchQuery
                              .trim()
                              .toLowerCase();
                            if (!q) {
                              return (
                                <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                                  Type to search contributions...
                                </div>
                              );
                            }
                            const filtered = availableContributions
                              .filter(
                                (cont) =>
                                  cont.toLowerCase().includes(q) &&
                                  !contributions.some(
                                    (c) => c.contribution === cont
                                  )
                              )
                              .slice(0, 50);
                            if (filtered.length === 0) {
                              return (
                                <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                                  No matching contributions
                                </div>
                              );
                            }
                            return filtered.map((cont) => (
                              <button
                                key={cont}
                                type="button"
                                className="tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-gray-700 tw-transition-colors"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setContributionModalForm({
                                    contributionName: cont,
                                    confidenceCategory: '5',
                                    evidenceUrl: '',
                                    notes: '',
                                  });
                                  setContributionModalEvidenceError(null);
                                  setContributionModalContext({
                                    type: 'add',
                                    contributionValue: cont,
                                  });
                                  setShowContributionModal(true);
                                  setAddContributionDropdownOpen(false);
                                  setContributionSearchQuery('');
                                }}
                              >
                                {capitalize(cont)}
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ISRCs (Multiple) */}
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="modal-isrcs-input"
                        className={`tw-block tw-text-sm tw-font-medium ${
                          isFieldModified('isrcs')
                            ? 'tw-text-orange-400'
                            : 'tw-text-white'
                        }`}
                      >
                        ISRCs
                      </label>
                    </div>
                    {isrcs.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                        {isrcs.map((isrc, index) => {
                          const isIsrcAdded =
                            !originalValues?.isrcs?.includes(isrc);
                          return (
                            <div
                              key={index}
                              className={`tw-relative tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white ${
                                !isRecordingEditable ? 'tw-opacity-60' : ''
                              }`}
                            >
                              {isIsrcAdded && (
                                <span className="tw-absolute -tw-top-1 -tw-right-1 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-orange-400 tw-border tw-border-gray-900 tw-animate-pulse-slow" />
                              )}
                              <span>{isrc}</span>
                              {isRecordingEditable && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    removeMultipleValue(setIsrcs, index);
                                    if (isrcFormatError)
                                      setIsrcFormatError(null);
                                  }}
                                  className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                                  title="Remove"
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                      <input
                        id="modal-isrcs-input"
                        type="text"
                        placeholder="Enter ISRC"
                        readOnly={!isRecordingEditable}
                        disabled={!isRecordingEditable}
                        onKeyDown={(e) => {
                          if (
                            isRecordingEditable &&
                            e.key === 'Enter' &&
                            e.currentTarget.value.trim()
                          ) {
                            addIsrc(e.currentTarget.value.trim());
                          }
                        }}
                        className={`tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                          !isRecordingEditable
                            ? 'tw-cursor-not-allowed tw-opacity-60'
                            : ''
                        }`}
                      />
                      {isRecordingEditable && (
                        <div className="tw-flex tw-justify-end">
                          <button
                            type="button"
                            className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                            onClick={() => {
                              const input = document.getElementById(
                                'modal-isrcs-input'
                              ) as HTMLInputElement;
                              if (input && input.value.trim()) {
                                addIsrc(input.value.trim());
                              }
                            }}
                          >
                            <i className="wi wi-20px wi-plus tw-mr-1" />
                            <span className="tw-underline">Add ISRC</span>
                          </button>
                        </div>
                      )}
                    </div>
                    {isrcFormatError && (
                      <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                        {isrcFormatError}
                      </p>
                    )}
                  </div>

                  {/* Duration, Live, Featured — same row */}
                  <div className="tw-flex tw-gap-4 tw-w-full">
                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                        <label
                          htmlFor="modal-duration"
                          className={`tw-block tw-text-sm tw-font-medium ${
                            isFieldModified('duration')
                              ? 'tw-text-orange-400'
                              : originalValues?.formData?.duration
                              ? 'tw-text-gray-400'
                              : 'tw-text-white'
                          }`}
                        >
                          Duration
                        </label>
                      </div>
                      <input
                        id="modal-duration"
                        type="text"
                        value={formData.duration || ''}
                        onChange={(e) =>
                          handleFieldChange('duration', e.target.value)
                        }
                        disabled={
                          !isRecordingEditable ||
                          !!originalValues?.formData?.duration
                            ?.toString()
                            .trim()
                        }
                        className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                          !originalValues?.formData?.duration
                            ?.toString()
                            .trim() &&
                          formData.duration &&
                          !isValidDuration(formData.duration)
                            ? 'tw-border-red-500'
                            : 'tw-border-gray-600'
                        }`}
                        placeholder="HH:MM:SS"
                      />
                      {!originalValues?.formData?.duration?.toString().trim() &&
                        formData.duration &&
                        !isValidDuration(formData.duration) && (
                          <p className="tw-text-red-400 tw-text-xs tw-mt-0.5">
                            Format: HH:MM:SS (e.g. 00:03:45)
                          </p>
                        )}
                    </div>

                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                        <label
                          htmlFor="modal-live"
                          className={`tw-block tw-text-sm tw-font-medium ${
                            isFieldModified('isLive')
                              ? 'tw-text-orange-400'
                              : 'tw-text-white'
                          }`}
                        >
                          Live
                        </label>
                      </div>
                      <select
                        id="modal-live"
                        value={formData.isLive ? 'Yes' : 'No'}
                        disabled={!isRecordingEditable}
                        onChange={(e) => {
                          const newLive = e.target.value === 'Yes';
                          if (!formData.isLive && newLive) {
                            setLiveToYesNote('');
                            setShowLiveToYesModal(true);
                          }
                        }}
                        className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                          isFieldModified('isLive')
                            ? 'tw-border-l-surfacePurple tw-border-l-[3px]'
                            : ''
                        }`}
                      >
                        <option value="No" disabled={formData.isLive}>
                          No
                        </option>
                        <option value="Yes">Yes</option>
                      </select>
                    </div>

                    <div className="tw-flex-1 tw-min-w-0">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                        <label
                          htmlFor="modal-featured"
                          className="tw-block tw-text-sm tw-font-medium tw-text-gray-400"
                        >
                          Featured
                        </label>
                      </div>
                      <select
                        id="modal-featured"
                        value={formData.isFeatured ? 'F' : 'NF'}
                        disabled
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-gray-400 tw-text-sm tw-cursor-not-allowed"
                      >
                        <option value="NF">Non-Featured (NF)</option>
                        <option value="F">Featured (F)</option>
                      </select>
                    </div>
                  </div>

                  {/* Edit Recording Details / Cancel / Save */}
                  <div className="tw-flex tw-gap-2 tw-items-center tw-mt-3 tw-pt-4 tw-border-t tw-border-gray-600 tw-pb-3">
                    {!isRecordingEditable && (
                      <WButton
                        variant="secondary"
                        label="Edit Recording Details"
                        onClick={() => setIsRecordingEditable(true)}
                      />
                    )}
                    {isRecordingEditable && (
                      <>
                        <WButton
                          variant="secondary"
                          label="Cancel"
                          onClick={handleRecordingCancel}
                        />
                        <WButton
                          variant="primary"
                          label={
                            recordingSaveLoading ? 'Saving…' : 'Save Changes'
                          }
                          onClick={handleSaveRecordingDetails}
                          disabled={recordingSaveLoading}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Separator */}
            <div
              className={
                stacked
                  ? 'tw-border-t tw-border-gray-600 tw-shrink-0'
                  : 'tw-border-l tw-border-gray-600'
              }
            />
          </>
        )}

        {/* Release Section */}
        <div
          className={
            stacked
              ? 'tw-p-3 tw-flex tw-flex-col tw-shrink-0'
              : 'tw-pr-4 tw-p-3 tw-flex tw-flex-col tw-overflow-y-auto'
          }
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-pb-1.5 tw-border-b tw-border-gray-600 tw-flex-shrink-0">
            <div className="tw-text-white tw-font-semibold">Release</div>
          </div>
          <div className="tw-space-y-4 tw-flex-1">
            {/* Release Title and Year of Release - same row */}
            <div className="tw-flex tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <label
                    htmlFor="modal-release-title"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('release')
                        ? 'tw-text-orange-400'
                        : 'tw-text-white'
                    }`}
                  >
                    Release Title
                  </label>
                </div>
                <input
                  id="modal-release-title"
                  type="text"
                  value={formData.release || ''}
                  onChange={(e) => handleFieldChange('release', e.target.value)}
                  readOnly={!isReleaseEditable}
                  className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                    !isReleaseEditable
                      ? 'tw-cursor-not-allowed tw-opacity-60'
                      : ''
                  }`}
                />
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <label
                    htmlFor="modal-year"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('year')
                        ? 'tw-text-orange-400'
                        : 'tw-text-white'
                    }`}
                  >
                    Year of Release
                  </label>
                </div>
                <input
                  id="modal-year"
                  type="text"
                  value={
                    formData.year
                      ? formData.year.length <= 4
                        ? formData.year
                        : new Date(formData.year).getFullYear().toString()
                      : ''
                  }
                  onChange={(e) => {
                    const yearValue = e.target.value.replace(/\D/g, '').slice(0, 4);
                    if (yearValue.length === 4) {
                      const date = new Date(`${yearValue}-01-01`);
                      handleFieldChange('year', date.toISOString());
                    } else {
                      handleFieldChange('year', yearValue);
                    }
                  }}
                  readOnly={!isReleaseEditable}
                  className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                    !isReleaseEditable
                      ? 'tw-cursor-not-allowed tw-opacity-60'
                      : ''
                  }`}
                  placeholder="YYYY"
                  maxLength={4}
                />
              </div>
            </div>

            {/* Label (Multiple) */}
            <div>
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <label
                  htmlFor="modal-label-input"
                  className={`tw-block tw-text-sm tw-font-medium ${
                    isFieldModified('label')
                      ? 'tw-text-orange-400'
                      : 'tw-text-white'
                  }`}
                >
                  Label (Multiple)
                </label>
                <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                  Optional
                </span>
              </div>
              {labels.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                  {labels.map((label, index) => (
                    <div
                      key={index}
                      className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white ${
                        !isReleaseEditable ? 'tw-opacity-60' : ''
                      }`}
                    >
                      <span>{label}</span>
                      {isReleaseEditable && (
                        <button
                          type="button"
                          onClick={() => removeMultipleValue(setLabels, index)}
                          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <input
                  id="modal-label-input"
                  type="text"
                  placeholder="Enter label"
                  readOnly={!isReleaseEditable}
                  disabled={!isReleaseEditable}
                  onKeyDown={(e) => {
                    if (
                      isReleaseEditable &&
                      e.key === 'Enter' &&
                      e.currentTarget.value.trim()
                    ) {
                      addMultipleValue(setLabels, e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                  className={`tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                    !isReleaseEditable
                      ? 'tw-cursor-not-allowed tw-opacity-60'
                      : ''
                  }`}
                />
                {isReleaseEditable && (
                  <div className="tw-flex tw-justify-end">
                    <button
                      type="button"
                      className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                      onClick={() => {
                        const input = document.getElementById(
                          'modal-label-input'
                        ) as HTMLInputElement;
                        if (input && input.value.trim()) {
                          addMultipleValue(setLabels, input.value);
                          input.value = '';
                        }
                      }}
                    >
                      <i className="wi wi-20px wi-plus tw-mr-1" />
                      <span className="tw-underline">Add label</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Cat no. (Multiple) */}
            <div>
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <label
                  htmlFor="modal-catno-input"
                  className={`tw-block tw-text-sm tw-font-medium ${
                    isFieldModified('catNumbers')
                      ? 'tw-text-orange-400'
                      : 'tw-text-white'
                  }`}
                >
                  Catalogue Number (Multiple)
                </label>
                <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                  Optional
                </span>
              </div>
              {catNumbers.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2 tw-min-h-[40px]">
                  {catNumbers.map((catNo, index) => (
                    <div
                      key={index}
                      className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white ${
                        !isReleaseEditable ? 'tw-opacity-60' : ''
                      }`}
                    >
                      <span>{catNo}</span>
                      {isReleaseEditable && (
                        <button
                          type="button"
                          onClick={() =>
                            removeMultipleValue(setCatNumbers, index)
                          }
                          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white tw-flex tw-items-center"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <input
                  id="modal-catno-input"
                  type="text"
                  placeholder="Enter catalogue number"
                  readOnly={!isReleaseEditable}
                  disabled={!isReleaseEditable}
                  onKeyDown={(e) => {
                    if (
                      isReleaseEditable &&
                      e.key === 'Enter' &&
                      e.currentTarget.value.trim()
                    ) {
                      addMultipleValue(setCatNumbers, e.currentTarget.value);
                      e.currentTarget.value = '';
                    }
                  }}
                  className={`tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                    !isReleaseEditable
                      ? 'tw-cursor-not-allowed tw-opacity-60'
                      : ''
                  }`}
                />
                {isReleaseEditable && (
                  <div className="tw-flex tw-justify-end">
                    <button
                      type="button"
                      className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                      onClick={() => {
                        const input = document.getElementById(
                          'modal-catno-input'
                        ) as HTMLInputElement;
                        if (input && input.value.trim()) {
                          addMultipleValue(setCatNumbers, input.value);
                          input.value = '';
                        }
                      }}
                    >
                      <i className="wi wi-20px wi-plus tw-mr-1" />
                      <span className="tw-underline">Add catalogue number</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Format and Type - same row */}
            <div className="tw-flex tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <label
                    htmlFor="modal-format"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('format')
                        ? 'tw-text-orange-400'
                        : 'tw-text-white'
                    }`}
                  >
                    Format
                  </label>
                </div>
                <select
                  id="modal-format"
                  value={formData.format || ''}
                  onChange={(e) => handleFieldChange('format', e.target.value)}
                  disabled={!isReleaseEditable}
                  className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                    isFieldModified('format')
                      ? 'tw-border-l-surfacePurple tw-border-l-[3px]'
                      : ''
                  }`}
                >
                  <option value="">Select Format</option>
                  {availableFormats.map((format) => (
                    <option key={format} value={format}>
                      {format}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <label
                    htmlFor="modal-type"
                    className={`tw-block tw-text-sm tw-font-medium ${
                      isFieldModified('mediaType')
                        ? 'tw-text-orange-400'
                        : 'tw-text-white'
                    }`}
                  >
                    Type
                  </label>
                </div>
                <select
                  id="modal-type"
                  value={formData.mediaType || ''}
                  onChange={(e) =>
                    handleFieldChange('mediaType', e.target.value)
                  }
                  disabled={!isReleaseEditable}
                  className={`tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                    isFieldModified('mediaType')
                      ? 'tw-border-l-surfacePurple tw-border-l-[3px]'
                      : ''
                  }`}
                >
                  <option value="">Select Type</option>
                  {availableTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Release Actions */}
            <div className="tw-flex tw-gap-2 tw-items-center tw-mt-3 tw-pt-4 tw-border-t tw-border-gray-600">
              {!isReleaseEditable && (
                <>
                  <WButton
                    variant="secondary"
                    label="Edit Release Details"
                    onClick={() => setShowReleaseCorrectionModal(true)}
                  />
                  <WButton
                    variant="secondary"
                    label="Change Linked Release"
                    onClick={() => setShowChangeReleaseModal(true)}
                  />
                </>
              )}
              {isReleaseEditable && (
                <>
                  <WButton
                    variant="secondary"
                    label="Cancel"
                    onClick={handleReleaseCancel}
                  />
                  <WButton
                    variant="primary"
                    label={
                      releaseSaveLoading ? 'Saving…' : 'Save Release Details'
                    }
                    onClick={handleSaveReleaseDetails}
                    disabled={releaseSaveLoading || !formData.release?.trim()}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Release Correction Confirmation Modal */}
      <WModal
        isOpen={showReleaseCorrectionModal}
        onClose={() => setShowReleaseCorrectionModal(false)}
        title="Confirm Release Correction"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            You are about to correct release data that is linked to multiple
            recordings. Do you wish to proceed?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => setShowReleaseCorrectionModal(false)}
            />
            <WButton
              variant="primary"
              label="Proceed"
              onClick={() => {
                setShowReleaseCorrectionModal(false);
                setIsReleaseEditable(true);
                // In a real implementation, this would trigger the release correction logic
                // For now, it's just a mock, so we enable editing
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Contribution Confidence modal */}
      <WModal
        isOpen={showContributionModal}
        onClose={() => {
          setShowContributionModal(false);
          setContributionModalContext(null);
          setContributionModalEvidenceError(null);
        }}
        title={`Contribution — ${
          contributionModalForm.contributionName
            ? capitalize(contributionModalForm.contributionName)
            : ''
        }`}
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4 tw-space-y-4">
          <div>
            <span className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
              Contribution
            </span>
            <p className="tw-text-white tw-text-sm">
              {contributionModalForm.contributionName
                ? capitalize(contributionModalForm.contributionName)
                : '—'}
            </p>
          </div>
          <div>
            <label
              htmlFor="contribution-modal-confidence"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Confidence category
            </label>
            <select
              id="contribution-modal-confidence"
              value={contributionModalForm.confidenceCategory}
              onChange={(e) =>
                setContributionModalForm((prev) => ({
                  ...prev,
                  confidenceCategory: e.target.value,
                }))
              }
              className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
            >
              {CONFIDENCE_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="contribution-modal-evidence"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Evidence URL <span className="tw-text-gray-400">(optional)</span>
            </label>
            <input
              id="contribution-modal-evidence"
              type="url"
              value={contributionModalForm.evidenceUrl}
              onChange={(e) => {
                setContributionModalForm((prev) => ({
                  ...prev,
                  evidenceUrl: e.target.value,
                }));
                if (contributionModalEvidenceError)
                  setContributionModalEvidenceError(null);
              }}
              placeholder="https://..."
              className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue ${
                contributionModalEvidenceError
                  ? 'tw-border-red-500'
                  : 'tw-border-gray-600'
              }`}
            />
            {contributionModalEvidenceError && (
              <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                {contributionModalEvidenceError}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="contribution-modal-notes"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Notes <span className="tw-text-gray-400">(optional)</span>
            </label>
            <textarea
              id="contribution-modal-notes"
              value={contributionModalForm.notes}
              onChange={(e) =>
                setContributionModalForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Optional notes"
              rows={3}
              className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue tw-resize-y"
            />
          </div>
          <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowContributionModal(false);
                setContributionModalContext(null);
                setContributionModalEvidenceError(null);
              }}
            />
            <WButton
              variant="primary"
              label="Save"
              onClick={() => {
                const evidenceUrl =
                  contributionModalForm.evidenceUrl?.trim() ?? '';
                setContributionModalEvidenceError(null);
                const payload = {
                  contribution: contributionModalForm.contributionName,
                  confidenceCategory: contributionModalForm.confidenceCategory,
                  confidencePattern: JSON.stringify({
                    evidenceUrl,
                    notes: contributionModalForm.notes?.trim() ?? '',
                  }),
                };
                if (contributionModalContext?.type === 'edit') {
                  setContributions((prev) =>
                    prev.map((c, i) =>
                      i === contributionModalContext.index ? payload : c
                    )
                  );
                } else if (contributionModalContext?.type === 'add') {
                  setContributions((prev) => [...prev, payload]);
                }
                setShowContributionModal(false);
                setContributionModalContext(null);
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Remove contribution confirmation modal */}
      <WModal
        isOpen={showRemoveContributionModal !== null}
        onClose={() => setShowRemoveContributionModal(null)}
        title="Remove Contribution"
        modalClasses="tw-max-w-sm tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            Are you sure you want to remove{' '}
            <strong>
              {showRemoveContributionModal !== null
                ? capitalize(
                    contributions[showRemoveContributionModal]?.contribution ??
                      ''
                  )
                : ''}
            </strong>{' '}
            from this recording?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => setShowRemoveContributionModal(null)}
            />
            <WButton
              variant="primary"
              label="Remove"
              onClick={() => {
                if (showRemoveContributionModal !== null) {
                  const removeIdx = showRemoveContributionModal;
                  setContributions((prev) =>
                    prev.filter((_, i) => i !== removeIdx)
                  );
                  if (
                    contributionModalContext?.type === 'edit' &&
                    contributionModalContext.index === removeIdx
                  ) {
                    setShowContributionModal(false);
                    setContributionModalContext(null);
                  } else if (
                    contributionModalContext?.type === 'edit' &&
                    contributionModalContext.index > removeIdx
                  ) {
                    setContributionModalContext({
                      type: 'edit',
                      index: contributionModalContext.index - 1,
                    });
                  }
                  setShowRemoveContributionModal(null);
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Artist actions modal (promote/demote/remove) */}
      <WModal
        isOpen={artistActionModalIndex !== null}
        onClose={() => setArtistActionModalIndex(null)}
        title="Manage Artist"
        modalClasses="tw-max-w-sm tw-w-full tw-mx-4"
      >
        {artistActionModalIndex !== null &&
          artists[artistActionModalIndex] &&
          (() => {
            const artist = artists[artistActionModalIndex];
            const idx = artistActionModalIndex;
            return (
              <div className="tw-mt-4 tw-space-y-4">
                <div>
                  <span className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                    Artist
                  </span>
                  <p className="tw-text-white tw-text-sm tw-font-medium">
                    {artist.name}
                  </p>
                </div>

                {/* Featured status */}
                <div>
                  <span className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-2">
                    Featured Status
                  </span>
                  <div className="tw-flex tw-flex-col tw-gap-1.5">
                    {(['fa', 'ofa', 'nf'] as FeaturedStatus[]).map((status) => (
                      <label
                        key={status}
                        className="tw-flex tw-items-center tw-gap-2 tw-text-white tw-text-sm tw-cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="manage-artist-featured"
                          checked={artist.featuredStatus === status}
                          onChange={() => {
                            setArtists((prev) =>
                              prev.map((a, i) =>
                                i === idx
                                  ? {
                                      ...a,
                                      isFeatured: status === 'fa',
                                      featuredStatus: status,
                                    }
                                  : a
                              )
                            );
                          }}
                        />
                        {status === 'fa'
                          ? 'Featuring Artist'
                          : status === 'ofa'
                          ? 'Other Featured Artist'
                          : 'Non-Featured'}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="tw-flex tw-justify-between tw-items-center tw-pt-3 tw-border-t tw-border-gray-700">
                  {artists.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => {
                        setArtists((prev) => prev.filter((_, i) => i !== idx));
                        setArtistActionModalIndex(null);
                      }}
                      className="tw-text-red-400 hover:tw-text-red-300 tw-text-sm tw-transition-colors"
                    >
                      Remove from recording
                    </button>
                  ) : (
                    <span className="tw-text-gray-600 tw-text-xs">
                      Cannot remove the only artist
                    </span>
                  )}
                  <WButton
                    variant="secondary"
                    label="Done"
                    onClick={() => setArtistActionModalIndex(null)}
                  />
                </div>
              </div>
            );
          })()}
      </WModal>

      {/* Duplicate ISRC confirmation modal */}
      <WModal
        isOpen={pendingDuplicateIsrc !== null}
        onClose={() => {
          setPendingDuplicateIsrc(null);
          setPendingDuplicateMatches([]);
        }}
        title="Reassign ISRC"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-3">
            This ISRC is currently assigned to:
          </p>
          {pendingDuplicateMatches.length > 0 && (
            <div className="tw-mb-4 tw-space-y-1.5 tw-max-h-32 tw-overflow-y-auto">
              {pendingDuplicateMatches.map((m) => (
                <div
                  key={m.recordingId}
                  className="tw-flex tw-flex-col tw-bg-gray-800 tw-rounded tw-px-3 tw-py-2 tw-text-xs"
                >
                  <span className="tw-text-white tw-font-medium">
                    {m.recordingTitle || 'Untitled'}
                  </span>
                  {m.artistName && (
                    <span className="tw-text-gray-400">{m.artistName}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="tw-text-white tw-text-sm">
            Continuing will remove the ISRC from{' '}
            {pendingDuplicateMatches.length === 1
              ? 'that recording'
              : 'those recordings'}{' '}
            and assign it to{' '}
            <strong>{formData.title || 'this recording'}</strong>.
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-5">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setPendingDuplicateIsrc(null);
                setPendingDuplicateMatches([]);
              }}
            />
            <WButton
              variant="primary"
              label="Continue"
              onClick={() => {
                if (pendingDuplicateIsrc) {
                  addMultipleValue(setIsrcs, pendingDuplicateIsrc);
                  const input = document.getElementById(
                    'modal-isrcs-input'
                  ) as HTMLInputElement;
                  if (input) input.value = '';
                  setPendingDuplicateIsrc(null);
                  setPendingDuplicateMatches([]);
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Create new artist modal (contract 4.4.2) */}
      <WModal
        isOpen={showCreateArtist}
        onClose={() => {
          setShowCreateArtist(false);
          setNewArtistAlias('');
          setNewArtistFeatured('fa');
          setNewArtistSource('discogs');
          setNewArtistSourceId('');
          setNewArtistIsGroup(false);
          setNewArtistMembers([]);
          setNewArtistMemberSearch('');
          setNewArtistSourceLookupError(null);
        }}
        title="Create new artist"
        modalClasses="tw-max-w-lg tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4 tw-space-y-4">
          {/* Source + Source ID — entered first to auto-populate stage name */}
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <div>
              <label
                htmlFor="create-artist-source"
                className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
              >
                Source
              </label>
              <select
                id="create-artist-source"
                value={newArtistSource}
                onChange={(e) => {
                  setNewArtistSource(e.target.value as typeof newArtistSource);
                  setNewArtistAlias('');
                  setNewArtistSourceId('');
                  setNewArtistSourceLookupError(null);
                }}
                className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
              >
                <option value="discogs">Discogs</option>
                <option value="MB">MusicBrainz</option>
                <option value="spotify">Spotify</option>
                <option value="deezer">Deezer</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="create-artist-source-id"
                className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
              >
                Source ID
              </label>
              <input
                id="create-artist-source-id"
                type="text"
                value={newArtistSourceId}
                onChange={(e) => {
                  setNewArtistSourceId(e.target.value);
                  if (!e.target.value.trim()) {
                    setNewArtistAlias('');
                    setNewArtistSourceLookupError(null);
                  }
                }}
                placeholder={
                  newArtistSource === 'discogs'
                    ? 'e.g. 12345'
                    : 'Enter source ID'
                }
                className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
              />
            </div>
          </div>

          {/* Stage Name — auto-populated from source lookup, read-only */}
          <div>
            <label
              htmlFor="create-artist-alias"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Stage Name
            </label>
            <div className="tw-relative">
              <input
                id="create-artist-alias"
                type="text"
                value={newArtistAlias}
                readOnly
                placeholder={
                  newArtistSourceLookupLoading
                    ? 'Looking up artist...'
                    : newArtistSourceId.trim()
                    ? 'Enter Source ID to auto-populate'
                    : 'Populated from Source ID lookup'
                }
                className={`tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-outline-none tw-cursor-default ${
                  newArtistAlias ? 'tw-text-white' : 'tw-text-gray-500'
                }`}
              />
              {newArtistSourceLookupLoading && (
                <span className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-text-xs">
                  Loading...
                </span>
              )}
            </div>
            {newArtistSourceLookupError && (
              <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                {newArtistSourceLookupError}
              </p>
            )}
            {newArtistSource !== 'discogs' && newArtistSourceId.trim() && (
              <p className="tw-text-gray-500 tw-text-xs tw-mt-1">
                Auto-lookup is only available for Discogs. For other sources,
                the name will be resolved by the backend.
              </p>
            )}
          </div>

          {/* Featured Status */}
          <div>
            <span className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2">
              Featured Status
            </span>
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              {(['fa', 'ofa', 'nf'] as FeaturedStatus[]).map((status) => (
                <label
                  key={status}
                  className="tw-flex tw-items-center tw-gap-2 tw-text-white tw-text-sm tw-cursor-pointer"
                >
                  <input
                    type="radio"
                    name="new-artist-featured"
                    checked={newArtistFeatured === status}
                    onChange={() => setNewArtistFeatured(status)}
                  />
                  {status === 'fa'
                    ? 'Featuring Artist'
                    : status === 'ofa'
                    ? 'Other Featured Artist'
                    : 'Non-Featured'}
                </label>
              ))}
            </div>
          </div>

          {/* Is Group toggle */}
          <label className="tw-flex tw-items-center tw-gap-2 tw-text-white tw-text-sm tw-cursor-pointer">
            <input
              type="checkbox"
              checked={newArtistIsGroup}
              onChange={(e) => {
                setNewArtistIsGroup(e.target.checked);
                if (!e.target.checked) {
                  setNewArtistMembers([]);
                  setNewArtistMemberSearch('');
                }
              }}
            />
            This is a group / band
          </label>

          {/* Members (when group) */}
          {newArtistIsGroup && (
            <div className="tw-border tw-border-gray-700 tw-rounded tw-p-3 tw-space-y-2">
              <span className="tw-text-white tw-text-sm tw-font-medium">
                Group Members
              </span>
              {newArtistMembers.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                  {newArtistMembers.map((m) => (
                    <div
                      key={m.id}
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                    >
                      <span>{m.stageName}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setNewArtistMembers((prev) =>
                            prev.filter((p) => p.id !== m.id)
                          )
                        }
                        className="tw-ml-0.5 tw-opacity-60 hover:tw-opacity-100 tw-flex tw-items-center"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="tw-relative">
                <input
                  type="text"
                  value={newArtistMemberSearch}
                  onChange={(e) => setNewArtistMemberSearch(e.target.value)}
                  placeholder="Search for member to add..."
                  className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1.5 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                />
                {newArtistMemberSearch.trim() && (
                  <div className="tw-absolute tw-z-50 tw-left-0 tw-right-0 tw-top-full tw-mt-0.5 tw-max-h-36 tw-overflow-y-auto tw-rounded tw-border tw-border-gray-600 tw-bg-gray-800 tw-py-1 tw-shadow-lg">
                    {newArtistMemberSearchLoading ? (
                      <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                        Searching...
                      </div>
                    ) : newArtistMemberResults.length === 0 ? (
                      <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">
                        No matching artists
                      </div>
                    ) : (
                      newArtistMemberResults
                        .filter(
                          (a) => !newArtistMembers.some((m) => m.id === a.id)
                        )
                        .map((artist) => (
                          <button
                            key={artist.id}
                            type="button"
                            className="tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-gray-700 tw-transition-colors"
                            onClick={() => {
                              setNewArtistMembers((prev) => [...prev, artist]);
                              setNewArtistMemberSearch('');
                            }}
                          >
                            <span>{artist.stageName}</span>
                            <span className="tw-text-gray-500 tw-text-xs tw-ml-2">
                              {artist.id}
                            </span>
                          </button>
                        ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2 tw-border-t tw-border-gray-700">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowCreateArtist(false);
                setNewArtistAlias('');
                setNewArtistFeatured('fa');
                setNewArtistSource('discogs');
                setNewArtistSourceId('');
                setNewArtistIsGroup(false);
                setNewArtistMembers([]);
                setNewArtistMemberSearch('');
                setNewArtistSourceLookupError(null);
              }}
            />
            <WButton
              variant="primary"
              label={
                newArtistSourceLookupLoading ? 'Looking up...' : 'Create & Add'
              }
              disabled={
                !newArtistSourceId.trim() ||
                !newArtistAlias.trim() ||
                newArtistSourceLookupLoading
              }
              onClick={async () => {
                if (newArtistAlias.trim() && newArtistSourceId.trim()) {
                  if (userId && row?.id) {
                    try {
                      await api.createArtistAndLinkServiceResponse({
                        userId,
                        parentRecordingId: row.id,
                        createArtistAndLinkRequest: {
                          stageName: newArtistAlias.trim(),
                          source:
                            newArtistSource as CreateArtistAndLinkRequestSourceEnum,
                          sourceId: newArtistSourceId.trim(),
                          featuredStatus:
                            newArtistFeatured === 'fa'
                              ? CreateArtistAndLinkRequestFeaturedStatusEnum.Fa
                              : CreateArtistAndLinkRequestFeaturedStatusEnum.Ofa,
                          members:
                            newArtistIsGroup && newArtistMembers.length > 0
                              ? newArtistMembers
                              : undefined,
                        },
                      });
                    } catch {
                      // API call failed — still add locally so user sees the change
                    }
                  }
                  setArtists((prev) => [
                    ...prev,
                    {
                      name: newArtistAlias.trim(),
                      isFeatured: newArtistFeatured === 'fa',
                      featuredStatus: newArtistFeatured,
                    },
                  ]);
                  setNewArtistAlias('');
                  setNewArtistFeatured('fa');
                  setNewArtistSource('discogs');
                  setNewArtistSourceId('');
                  setNewArtistIsGroup(false);
                  setNewArtistMembers([]);
                  setNewArtistMemberSearch('');
                  setNewArtistSourceLookupError(null);
                  setShowCreateArtist(false);
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Add existing artist modal (featured selection) */}
      <WModal
        isOpen={pendingArtistName !== null}
        onClose={() => {
          setPendingArtistName(null);
          setPendingArtistId(null);
          setPendingArtistFeatured('fa');
        }}
        title="Add artist"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4 tw-space-y-4">
          <div>
            <span className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
              Artist
            </span>
            <p className="tw-text-white tw-text-sm">
              {pendingArtistName ?? ''}
            </p>
          </div>
          <div>
            <span className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-2">
              Featured Status
            </span>
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              {(['fa', 'ofa', 'nf'] as FeaturedStatus[]).map((status) => (
                <label
                  key={status}
                  className="tw-flex tw-items-center tw-gap-2 tw-text-white tw-text-sm tw-cursor-pointer"
                >
                  <input
                    type="radio"
                    name="pending-artist-featured"
                    checked={pendingArtistFeatured === status}
                    onChange={() => setPendingArtistFeatured(status)}
                  />
                  {status === 'fa'
                    ? 'Featuring Artist'
                    : status === 'ofa'
                    ? 'Other Featured Artist'
                    : 'Non-Featured'}
                </label>
              ))}
            </div>
          </div>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setPendingArtistName(null);
                setPendingArtistId(null);
                setPendingArtistFeatured('fa');
              }}
            />
            <WButton
              variant="primary"
              label="Add"
              onClick={() => {
                if (pendingArtistName) {
                  setArtists((prev) => [
                    ...prev,
                    {
                      name: pendingArtistName,
                      isFeatured: pendingArtistFeatured === 'fa',
                      featuredStatus: pendingArtistFeatured,
                      id: pendingArtistId ?? undefined,
                    },
                  ]);
                  setPendingArtistName(null);
                  setPendingArtistId(null);
                  setPendingArtistFeatured('fa');
                }
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Change Release Modal */}
      <WModal
        isOpen={showChangeReleaseModal}
        onClose={() => {
          setShowChangeReleaseModal(false);
          resetReleaseModalState();
        }}
        title={
          releaseSource === 'Other' || releaseLookupNotFound
            ? 'Create and Link Release'
            : 'Change Linked Release'
        }
        modalClasses="tw-max-w-lg tw-w-full tw-mx-4 tw-max-h-[90vh]"
      >
        <div className="tw-mt-4 tw-overflow-y-auto tw-max-h-[calc(90vh-120px)]">
          <div className="tw-mb-4">
            <label
              htmlFor="release-source-input"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Release Source
            </label>
            <select
              id="release-source-input"
              value={releaseSource}
              onChange={(e) => {
                const newSource = e.target.value as
                    | 'Discogs'
                    | 'Deezer'
                    | 'Spotify'
                    | 'MusicBrainz'
                    | 'PPL'
                    | 'Other';
                setReleaseSource(newSource);
                setReleaseId('');
                setSourceUrl('');
                setReleaseLookupError(null);
                setReleaseLookupSuccess(false);
                setReleaseLookupNotFound(false);
                setReleaseLookupNotLinked(false);
                setReleaseNotLinkedConfirmed(false);
                if (newSource === 'Other' && !newReleaseTitle.trim()) {
                  // Pre-populate from the currently linked release when fields are empty
                  setNewReleaseTitle(formData.release || '');
                  setNewReleaseYear(
                    formData.year
                      ? formData.year.length <= 4
                        ? formData.year
                        : new Date(formData.year).getFullYear().toString()
                      : ''
                  );
                  setNewReleaseLabels([...labels]);
                  setNewReleaseCatNumbers([...catNumbers]);
                  setNewReleaseFormat(formData.format || '');
                  setNewReleaseType(formData.mediaType || '');
                }
              }}
              className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
            >
              <option value="Discogs">Discogs</option>
              <option value="Deezer">Deezer</option>
              <option value="Spotify">Spotify</option>
              <option value="MusicBrainz">MusicBrainz</option>
              <option value="PPL">PPL</option>
              <option value="Other">Other</option>
            </select>
          </div>
          {['Discogs', 'Deezer', 'Spotify', 'MusicBrainz', 'PPL'].includes(
            releaseSource
          ) ? (
            <div className="tw-mb-4">
              <label
                htmlFor="release-id-input"
                className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
              >
                Release ID
              </label>
              <div className="tw-flex tw-gap-2 tw-items-center">
                <input
                  id="release-id-input"
                  type="text"
                  value={releaseId}
                  onChange={(e) => {
                    setReleaseId(e.target.value);
                    setReleaseLookupError(null);
                    setReleaseLookupSuccess(false);
                    setReleaseLookupNotFound(false);
                    setReleaseLookupNotLinked(false);
                    setReleaseNotLinkedConfirmed(false);
                  }}
                  className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  placeholder="Enter release ID"
                />
                <WButton
                  variant="secondary"
                  label={releaseLookupLoading ? 'Looking up…' : 'Lookup'}
                  disabled={
                    releaseLookupLoading ||
                    !releaseId.trim() ||
                    !userId ||
                    !row?.id
                  }
                  onClick={handleReleaseLookup}
                />
              </div>
              {releaseLookupError && (
                <p className="tw-text-westburyRed tw-text-xs tw-mt-2">
                  {releaseLookupError}
                </p>
              )}
              {releaseLookupNotFound && (
                <div className="tw-mt-4 tw-space-y-4 tw-pt-4 tw-border-t tw-border-gray-600">
                  <p className="tw-text-orange-400 tw-text-sm">
                    Release not found in WMC database, please provide details.
                  </p>
                  <div className="tw-flex tw-gap-4 tw-w-full">
                    <div className="tw-flex-1 tw-min-w-0">
                      <label
                        htmlFor="change-release-notfound-title"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                      >
                        Release Title
                      </label>
                      <input
                        id="change-release-notfound-title"
                        type="text"
                        value={newReleaseTitle}
                        onChange={(e) => setNewReleaseTitle(e.target.value)}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                        placeholder="Enter release title"
                      />
                    </div>
                    <div className="tw-flex-1 tw-min-w-0">
                      <label
                        htmlFor="change-release-notfound-year"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                      >
                        Year of Release
                      </label>
                      <input
                        id="change-release-notfound-year"
                        type="text"
                        value={newReleaseYear}
                        onChange={(e) => setNewReleaseYear(e.target.value)}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                        placeholder="YYYY"
                        maxLength={4}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label className="tw-block tw-text-white tw-text-sm tw-font-medium">
                        Label (Multiple)
                      </label>
                      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                        Optional
                      </span>
                    </div>
                    {newReleaseLabels.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
                        {newReleaseLabels.map((label, index) => (
                          <div
                            key={index}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                          >
                            <span>{label}</span>
                            <button
                              type="button"
                              onClick={() =>
                                setNewReleaseLabels((prev) =>
                                  prev.filter((_, i) => i !== index)
                                )
                              }
                              className="tw-ml-1 tw-text-gray-400 hover:tw-text-white"
                              title="Remove"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                      <input
                        id="change-release-notfound-labels-input"
                        type="text"
                        placeholder="Enter label"
                        onKeyDown={(e) => {
                          if (
                            e.key === 'Enter' &&
                            e.currentTarget.value.trim()
                          ) {
                            addMultipleValue(
                              setNewReleaseLabels,
                              e.currentTarget.value
                            );
                            e.currentTarget.value = '';
                          }
                        }}
                        className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      />
                      <div className="tw-flex tw-justify-end">
                        <button
                          type="button"
                          className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                          onClick={() => {
                            const input = document.getElementById(
                              'change-release-notfound-labels-input'
                            ) as HTMLInputElement;
                            if (input?.value.trim()) {
                              addMultipleValue(
                                setNewReleaseLabels,
                                input.value
                              );
                              input.value = '';
                            }
                          }}
                        >
                          <i className="wi wi-20px wi-plus tw-mr-1" />
                          <span className="tw-underline">Add label</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label className="tw-block tw-text-white tw-text-sm tw-font-medium">
                        Catalogue Number (Multiple)
                      </label>
                      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                        Optional
                      </span>
                    </div>
                    {newReleaseCatNumbers.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
                        {newReleaseCatNumbers.map((catNo, index) => (
                          <div
                            key={index}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                          >
                            <span>{catNo}</span>
                            <button
                              type="button"
                              onClick={() =>
                                setNewReleaseCatNumbers((prev) =>
                                  prev.filter((_, i) => i !== index)
                                )
                              }
                              className="tw-ml-1 tw-text-gray-400 hover:tw-text-white"
                              title="Remove"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                      <input
                        id="change-release-notfound-catno-input"
                        type="text"
                        placeholder="Enter catalogue number"
                        onKeyDown={(e) => {
                          if (
                            e.key === 'Enter' &&
                            e.currentTarget.value.trim()
                          ) {
                            addMultipleValue(
                              setNewReleaseCatNumbers,
                              e.currentTarget.value
                            );
                            e.currentTarget.value = '';
                          }
                        }}
                        className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      />
                      <div className="tw-flex tw-justify-end">
                        <button
                          type="button"
                          className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                          onClick={() => {
                            const input = document.getElementById(
                              'change-release-notfound-catno-input'
                            ) as HTMLInputElement;
                            if (input?.value.trim()) {
                              addMultipleValue(
                                setNewReleaseCatNumbers,
                                input.value
                              );
                              input.value = '';
                            }
                          }}
                        >
                          <i className="wi wi-20px wi-plus tw-mr-1" />
                          <span className="tw-underline">
                            Add catalogue number
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="tw-flex tw-gap-4 tw-w-full">
                    <div className="tw-flex-1 tw-min-w-0">
                      <label
                        htmlFor="change-release-notfound-format"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                      >
                        Format
                      </label>
                      <select
                        id="change-release-notfound-format"
                        value={newReleaseFormat}
                        onChange={(e) => setNewReleaseFormat(e.target.value)}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      >
                        <option value="">Select Format</option>
                        {availableFormats.map((format) => (
                          <option key={format} value={format}>
                            {format}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="tw-flex-1 tw-min-w-0">
                      <label
                        htmlFor="change-release-notfound-type"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                      >
                        Type
                      </label>
                      <select
                        id="change-release-notfound-type"
                        value={newReleaseType}
                        onChange={(e) => setNewReleaseType(e.target.value)}
                        className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                      >
                        <option value="">Select Type</option>
                        {availableTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
              {releaseLookupSuccess && (
                <div className="tw-mt-4 tw-space-y-4 tw-pt-4 tw-border-t tw-border-gray-600">
                  <div className="tw-flex tw-gap-4 tw-w-full">
                    <div className="tw-flex-1">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Release Title
                      </label>
                      <input
                        type="text"
                        value={newReleaseTitle}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                    <div className="tw-max-w-28">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Year of Release
                      </label>
                      <input
                        type="text"
                        value={newReleaseYear}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                      Label (Multiple)
                    </label>
                    <div className="tw-min-h-[40px] tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
                      {newReleaseLabels.length > 0 ? (
                        newReleaseLabels.map((label, index) => (
                          <span
                            key={index}
                            className="tw-inline-flex tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700"
                          >
                            {label}
                          </span>
                        ))
                      ) : (
                        <span className="tw-text-gray-500">—</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                      Catalogue Number (Multiple)
                    </label>
                    {newReleaseCatNumbers.length > 0 && (
                      <div className="tw-min-h-[40px] tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
                        {newReleaseCatNumbers.length > 0 ? (
                          newReleaseCatNumbers.map((catNo, index) => (
                            <span
                              key={index}
                              className="tw-inline-flex tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700"
                            >
                              {catNo}
                            </span>
                          ))
                        ) : (
                          <span className="tw-text-gray-500">—</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="tw-flex tw-gap-4 tw-w-full">
                    <div className="tw-w-1/2">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Format
                      </label>
                      <input
                        type="text"
                        value={newReleaseFormat}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                    <div className="tw-w-1/2">
                      <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                        Type
                      </label>
                      <input
                        type="text"
                        value={newReleaseType}
                        readOnly
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="tw-space-y-4">
              <div>
                <label
                  htmlFor="new-release-source-url"
                  className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                >
                  Source
                </label>
                <input
                  id="new-release-source-url"
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => {
                    setSourceUrl(e.target.value);
                  }}
                  className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  placeholder="Provide release source details"
                />
              </div>
              <div className="tw-flex tw-gap-4 tw-w-full">
                <div className="tw-flex-1 tw-min-w-0">
                  <label
                    htmlFor="new-release-title"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                  >
                    Release Title
                  </label>
                  <input
                    id="new-release-title"
                    type="text"
                    value={newReleaseTitle}
                    onChange={(e) => setNewReleaseTitle(e.target.value)}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                    placeholder="Enter release title"
                  />
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <label
                    htmlFor="new-release-year"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                  >
                    Year of Release
                  </label>
                  <input
                    id="new-release-year"
                    type="text"
                    value={newReleaseYear}
                    onChange={(e) => setNewReleaseYear(e.target.value)}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                    placeholder="YYYY"
                    maxLength={4}
                  />
                </div>
              </div>
              <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-labels-input"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium"
                  >
                    Label (Multiple)
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                </div>
                {newReleaseLabels.length > 0 && (
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
                    {newReleaseLabels.map((label, index) => (
                      <div
                        key={index}
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                      >
                        <span>{label}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setNewReleaseLabels((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <input
                    id="new-release-labels-input"
                    type="text"
                    placeholder="Enter label"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        addMultipleValue(
                          setNewReleaseLabels,
                          e.currentTarget.value
                        );
                        e.currentTarget.value = '';
                      }
                    }}
                    className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  />
                  <div className="tw-flex tw-justify-end">
                    <button
                      type="button"
                      className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                      onClick={() => {
                        const input = document.getElementById(
                          'new-release-labels-input'
                        ) as HTMLInputElement;
                        if (input?.value.trim()) {
                          addMultipleValue(setNewReleaseLabels, input.value);
                          input.value = '';
                        }
                      }}
                    >
                      <i className="wi wi-20px wi-plus tw-mr-1" />
                      <span className="tw-underline">Add label</span>
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-catno-input"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium"
                  >
                    Catalogue Number (Multiple)
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                </div>
                {newReleaseCatNumbers.length > 0 && (
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
                    {newReleaseCatNumbers.map((catNo, index) => (
                      <div
                        key={index}
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
                      >
                        <span>{catNo}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setNewReleaseCatNumbers((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="tw-ml-1 tw-text-gray-400 hover:tw-text-white"
                          title="Remove"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <input
                    id="new-release-catno-input"
                    type="text"
                    placeholder="Enter catalogue number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        addMultipleValue(
                          setNewReleaseCatNumbers,
                          e.currentTarget.value
                        );
                        e.currentTarget.value = '';
                      }
                    }}
                    className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-2 tw-py-1 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  />
                  <div className="tw-flex tw-justify-end">
                    <button
                      type="button"
                      className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
                      onClick={() => {
                        const input = document.getElementById(
                          'new-release-catno-input'
                        ) as HTMLInputElement;
                        if (input?.value.trim()) {
                          addMultipleValue(
                            setNewReleaseCatNumbers,
                            input.value
                          );
                          input.value = '';
                        }
                      }}
                    >
                      <i className="wi wi-20px wi-plus tw-mr-1" />
                      <span className="tw-underline">Add catalogue number</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="tw-flex tw-gap-4 tw-w-full">
                <div className="tw-flex-1 tw-min-w-0">
                  <label
                    htmlFor="new-release-format"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                  >
                    Format
                  </label>
                  <select
                    id="new-release-format"
                    value={newReleaseFormat}
                    onChange={(e) => setNewReleaseFormat(e.target.value)}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  >
                    <option value="">Select Format</option>
                    {availableFormats.map((format) => (
                      <option key={format} value={format}>
                        {format}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <label
                    htmlFor="new-release-type"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                  >
                    Type
                  </label>
                  <select
                    id="new-release-type"
                    value={newReleaseType}
                    onChange={(e) => setNewReleaseType(e.target.value)}
                    className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
                  >
                    <option value="">Select Type</option>
                    {availableTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          {releaseLookupNotLinked && (
            <div
              className="tw-rounded-lg tw-border tw-border-orange-500/60 tw-bg-orange-500/10 tw-p-4"
              role="alert"
            >
              <p className="tw-text-orange-400 tw-text-sm tw-font-medium tw-mb-2">
                Confirm Release Linking
              </p>
              <p className="tw-text-gray-300 tw-text-sm tw-mb-4">
                This release is already linked to a different recording.
              </p>
              <p className="tw-text-gray-300 tw-text-sm tw-mb-4">
                Please double-check that you are linking it to the correct
                recording and not a different version.
              </p>
              <label
                htmlFor="change-release-not-linked-confirmed"
                className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer"
              >
                <input
                  id="change-release-not-linked-confirmed"
                  type="checkbox"
                  checked={releaseNotLinkedConfirmed}
                  onChange={(e) =>
                    setReleaseNotLinkedConfirmed(e.target.checked)
                  }
                  className="tw-mt-0.5 tw-rounded tw-border-gray-600 tw-bg-gray-700 tw-text-orange-500 focus:tw-ring-orange-500"
                />
                <span className="tw-text-sm tw-text-white">
                  I have read and understood this warning
                </span>
              </label>
            </div>
          )}
          {releaseChangeSubmitError && (
            <p className="tw-text-westburyRed tw-text-xs tw-mt-2 tw-mb-2">
              {releaseChangeSubmitError}
            </p>
          )}
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-600">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowChangeReleaseModal(false);
                resetReleaseModalState();
              }}
            />
            <WButton
              variant="primary"
              label={
                releaseChangeSubmitLoading
                  ? 'Saving…'
                  : releaseSource === 'Other' || releaseLookupNotFound
                  ? 'Create and Link Release'
                  : 'Change Linked Release'
              }
              disabled={
                releaseChangeSubmitLoading ||
                (releaseSource === 'Other' || releaseLookupNotFound
                  ? !newReleaseTitle.trim() ||
                    (releaseSource === 'Other' && !sourceUrl.trim())
                  : !releaseLookupSuccess ||
                    !lookupReleaseId ||
                    (releaseLookupNotLinked && !releaseNotLinkedConfirmed))
              }
              onClick={handleChangeReleaseSubmit}
            />
          </div>
        </div>
      </WModal>

      {/* Live N → Y note modal */}
      <WModal
        isOpen={showLiveToYesModal}
        onClose={() => {
          setShowLiveToYesModal(false);
          setLiveToYesNote('');
        }}
        title="Mark Recording as Live"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-4">
            Provide Live information for this recording. This will be stored as
            the live descriptor and appended to the Version field.
          </p>
          <label
            htmlFor="live-to-yes-note"
            className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
          >
            Live Information <span className="tw-text-red-400">(required)</span>
          </label>
          <textarea
            id="live-to-yes-note"
            value={liveToYesNote}
            onChange={(e) => setLiveToYesNote(e.target.value)}
            placeholder="e.g. Live in London, 1989"
            rows={3}
            className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue tw-resize-y"
          />
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowLiveToYesModal(false);
                setLiveToYesNote('');
              }}
            />
            <WButton
              variant="primary"
              label="Confirm"
              disabled={!liveToYesNote.trim()}
              onClick={() => {
                handleFieldChange('isLive', true);
                // Append the note to the Version field
                const note = liveToYesNote.trim();
                setVersions((prev) =>
                  prev.length > 0 ? [...prev, note] : [note]
                );
                setShowLiveToYesModal(false);
                // Don't clear liveToYesNote here — it's read by handleSaveRecordingDetails
                // to send as the "live" field value. Cleared on save/cancel.
              }}
            />
          </div>
        </div>
      </WModal>

      {/* Revert Confirmation Modal */}
      <WModal
        isOpen={showRevertModal}
        onClose={() => {
          setShowRevertModal(false);
          setRevertTarget(null);
        }}
        title="Confirm Revert"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            Do you want to revert to the original value?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setShowRevertModal(false);
                setRevertTarget(null);
              }}
            />
            <WButton
              variant="primary"
              label="Revert"
              onClick={() => {
                if (revertTarget) {
                  handleRevert(revertTarget.type, revertTarget.field);
                }
              }}
            />
          </div>
        </div>
      </WModal>
    </>
  );
});
